import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import CalendarGridView from './CalendarGridView';
import { GameEditDialog } from './TeamWeekView';
import ScheduleNotesCell from './ScheduleNotesCell';
import ScheduleSettingsDialog from './ScheduleSettingsDialog';
import OlImporter from './admin/OlImporter';
import AddTeamModal from './teams/AddTeamModal';
import AddVenueModal from './teams/AddVenueModal';
import {
  Plus, Trash2, Loader2, Save, CheckCircle2, XCircle, Edit2,
  MapPin, Clock, Home, Plane, Users, Trophy, Check, X,
  ChevronDown, ChevronUp, Filter, Globe, Calendar, List,
  Send, Eye, EyeOff, Info, Mail, Settings as SettingsIcon,
  Lock, Trophy as TrophyIcon, ChevronsUpDown, RefreshCw, CalendarClock, Database
} from 'lucide-react';

const ADD_NEW = '__add_new__';   // "+ Add New" option value in the Opponent / Location pickers

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Normalize a time string for display. Any of these inputs render as "TBD":
 *   "", null, undefined, "TBD", "TBA", "12:00 AM" (the Google-Sheet convention),
 *   "0:00", "00:00".
 * Everything else is returned unchanged (e.g. "7:15 PM").
 *
 * Note: when pushing to WP these should map to the bridge's `time_tba` field
 * ("TBD" | "TBA" | "") per /app/OL Migration/DATA_SCHEMAS.md §Game. `unix_dtg`
 * stays as a numeric placeholder (midnight in the org timezone) when TBD.
 */
function displayTime(t) {
  if (t == null) return 'TBD';
  const s = String(t).trim().toLowerCase();
  if (s === '' || s === 'tbd' || s === 'tba' || s === '12:00 am' || s === '0:00' || s === '00:00') {
    return 'TBD';
  }
  return t;
}
function isTbdTime(t) {
  return displayTime(t) === 'TBD';
}

/**
 * Convert a stored 12h time string like "6:00 PM" into "18:00" for <input type="time">.
 * Returns "" for TBD/empty so the input renders cleanly empty.
 */
function to24h(stored) {
  if (!stored || isTbdTime(stored)) return '';
  const m = String(stored).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ap = (m[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
}

/** Convert "18:00" from <input type="time"> back to the stored "6:00 PM" format. */
function to12h(hhmm) {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mStr} ${ampm}`;
}

function getDayOfWeek(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return DAYS[d.getDay()] || '';
}

// ---------------------------------------------------------------------------
// Inline-edit cell primitives (Excel-style)
//
// Click → cell turns into the matching input, focus moves there. Save
// fires on blur (or Enter); Esc reverts. The save callback is the
// parent's ``patchSlotField(slotId, field, value)`` which issues a
// one-key PATCH against ``/api/game-schedule/{orgId}/{slotId}``.
//
// Save state lives PER CELL — multiple cells on the same row can be
// in-flight simultaneously without blocking each other. Errors surface
// as a red ring around the cell + a tiny tooltip; the per-row error
// bar is rendered by the table parent (it aggregates errors across
// every cell on the row).
//
// Why not reuse the WP Mgmt EditableCell? Because GameSchedulePanel
// has its own table (intentionally — it groups by date, has 8+ team-
// time columns, and a non-trivial row-expand pattern). Sharing a
// component would force generic abstractions that hurt both call
// sites; copying the 60-line cell primitives here is cheaper and
// keeps each panel's UX tunable.
// ---------------------------------------------------------------------------
function InlineCell({ children, onClick, isDark, busy, error, testId, title }) {
  const cls = `cursor-text -mx-1 px-1 py-0.5 rounded transition-colors
    ${isDark ? 'hover:bg-slate-700/40' : 'hover:bg-slate-200/60'}
    ${error ? 'ring-1 ring-red-500/60 bg-red-900/15' : ''}`;
  return (
    <span className={cls} onClick={onClick} data-testid={testId} title={title || error || undefined}>
      {children}
      {busy && <Loader2 className="inline-block h-3 w-3 ml-1 animate-spin text-amber-400" />}
    </span>
  );
}

function InlineText({ value, onSave, isDark, testId, placeholder, mono }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      try { ref.current.select(); } catch {}
    }
  }, [editing]);

  const commit = async () => {
    if (draft === (value ?? '')) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
      // Stay in edit mode so the user can fix and retry; the red
      // ring + tooltip already telegraph the problem.
    } finally {
      setBusy(false);
    }
  };
  const cancel = () => { setDraft(value ?? ''); setEditing(false); setError(null); };

  if (!editing) {
    return (
      <InlineCell onClick={() => setEditing(true)} isDark={isDark} busy={busy} error={error} testId={testId}>
        <span className={mono ? 'font-mono text-xs' : ''}>{value || <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>{placeholder || '—'}</span>}</span>
      </InlineCell>
    );
  }
  return (
    <Input ref={ref} value={draft} onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
      onClick={e => e.stopPropagation()}
      disabled={busy}
      className={`h-7 px-1 text-xs ${error ? 'border-red-500 ring-1 ring-red-500/40' : 'border-amber-500/60'} ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'} ${mono ? 'font-mono' : ''}`}
      data-testid={`${testId}-input`}
      placeholder={placeholder}
    />
  );
}

// Inline strict-SELECT cell: a native dropdown restricted to existing options
// (NO free typing) so the saved value ALWAYS exactly matches an entry stored in
// the DB. Used for Opponent + Location. If the current value isn't in the list
// it's shown as a (current) option so the cell never blanks, but the user can
// only switch to a real list entry.
function InlineListSelect({ value, onSave, isDark, testId, placeholder, options, readOnly, emptyLabel, onAddNew, addLabel }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => { if (editing && ref.current) { try { ref.current.focus(); } catch {} } }, [editing]);

  const commit = async (next) => {
    if (next === (value ?? '')) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (readOnly) {
    return <span className="px-1" data-testid={testId}>{value || (emptyLabel ? <span>{emptyLabel}</span> : <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>{placeholder || '—'}</span>)}</span>;
  }
  if (!editing) {
    return (
      <InlineCell onClick={() => setEditing(true)} isDark={isDark} busy={busy} error={error} testId={testId}>
        <span>{value || (emptyLabel ? <span>{emptyLabel}</span> : <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>{placeholder || '—'}</span>)}</span>
      </InlineCell>
    );
  }
  const inList = (options || []).includes(value);
  return (
    <select ref={ref} defaultValue={value ?? ''}
      onChange={e => {
        if (e.target.value === ADD_NEW) { setEditing(false); onAddNew?.(); return; }
        commit(e.target.value);
      }}
      onBlur={() => setEditing(false)}
      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setEditing(false); } }}
      onClick={e => e.stopPropagation()}
      disabled={busy}
      className={`h-7 max-w-full px-1 text-xs rounded border ${error ? 'border-red-500 ring-1 ring-red-500/40' : 'border-amber-500/60'} ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'}`}
      data-testid={`${testId}-input`}>
      <option value="">{placeholder || '— none —'}</option>
      {onAddNew && <option value={ADD_NEW}>{addLabel || '+ Add New…'}</option>}
      {!inList && value && <option value={value}>{value} (current)</option>}
      {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}


function InlineDate({ value, onSave, isDark, testId, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const commit = async () => {
    if (draft === (value || '')) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setBusy(false); }
  };

  if (readOnly) {
    return <span className="font-mono text-xs px-1" data-testid={testId}>{value || '—'}</span>;
  }
  if (!editing) {
    return (
      <InlineCell onClick={() => setEditing(true)} isDark={isDark} busy={busy} error={error} testId={testId}>
        <span className="font-mono text-xs">{value || '—'}</span>
      </InlineCell>
    );
  }
  return (
    <Input ref={ref} type="date" value={draft} onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); setDraft(value || ''); setEditing(false); setError(null); }
      }}
      onClick={e => e.stopPropagation()}
      disabled={busy}
      className={`h-7 px-1 text-xs font-mono ${error ? 'border-red-500' : 'border-amber-500/60'} ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'}`}
      data-testid={`${testId}-input`}
    />
  );
}

function InlineSelect({ value, options, onSave, isDark, testId, render, readOnly }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = async (newVal) => {
    if (newVal === value) return;
    setBusy(true); setError(null);
    try { await onSave(newVal); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || 'Save failed'); }
    finally { setBusy(false); }
  };
  if (readOnly) {
    return <span data-testid={testId}>{render ? render(value) : <span>{value}</span>}</span>;
  }
  return (
    <span className={`relative inline-block ${error ? 'ring-1 ring-red-500/60 rounded' : ''}`} title={error || undefined}>
      {render ? render(value) : <span>{value}</span>}
      <select value={value || ''} onChange={e => handleChange(e.target.value)}
        onClick={e => e.stopPropagation()}
        disabled={busy}
        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
        data-testid={`${testId}-select`}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {busy && <Loader2 className="inline-block h-3 w-3 ml-1 animate-spin text-amber-400" />}
    </span>
  );
}

// Per-team kickoff time. Displays the existing 12h/TBD pill until
// clicked, then swaps to a native <input type="time"> for click-and-
// type editing. Save on blur, like every other inline cell in the
// app. Empty time clears the team's slot (matches /team-time PUT
// semantics: empty string ⇒ remove team from slot.games[]).
// "6:00 PM" -> minutes from midnight; TBD / 12:00 AM / blank -> null (the editor's tip-off field).
function timeToMin(t) {
  const m = String(t || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = +m[1]; const min = +m[2]; const ap = (m[3] || '').toUpperCase();
  if (ap) { if (h === 12) h = 0; if (ap === 'PM') h += 12; }
  return (h === 0 && min === 0) ? null : h * 60 + min;
}

function InlineTeamTime({ time, onSave, isDark, testId, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(to24h(time));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => { if (!editing) setDraft(to24h(time)); }, [time, editing]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const commit = async () => {
    if (draft === to24h(time)) { setEditing(false); return; }
    setBusy(true); setError(null);
    try { await onSave(draft); setEditing(false); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || 'Save failed'); }
    finally { setBusy(false); }
  };
  const cancel = () => { setDraft(to24h(time)); setEditing(false); setError(null); };

  if (editing) {
    return (
      <input ref={ref} type="time" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        onClick={e => e.stopPropagation()}
        disabled={busy}
        className={`h-7 w-full text-xs px-1 rounded ${error ? 'border-red-500 ring-1 ring-red-500/40' : 'border-amber-500/60'} border ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'}`}
        data-testid={`${testId}-input`}
      />
    );
  }
  const pill = time != null ? (
    <span className={`text-xs px-1.5 py-0.5 rounded ${
      isTbdTime(time)
        ? (isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700')
        : (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
    }`}>{displayTime(time)}</span>
  ) : (
    <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>-</span>
  );
  if (readOnly) {
    return <span data-testid={testId}>{pill}</span>;
  }
  return (
    <span onClick={e => { e.stopPropagation(); setEditing(true); }}
      data-testid={testId}
      title={error || (time == null ? 'Click to set kickoff time' : 'Click to edit kickoff time')}
      className={`cursor-pointer inline-block ${error ? 'ring-1 ring-red-500/60 rounded' : ''}`}>
      {pill}
      {busy && <Loader2 className="inline-block h-3 w-3 ml-1 animate-spin text-amber-400" />}
    </span>
  );
}

export default function GameSchedulePanel({ orgId, onSeasonWindow, initialView = 'list', mode = 'game',
                                            readOnly = false, lockedSport = '' }) {
  // ``lockedSport`` pins this panel to ONE sport (a 2-char code: bk/bb/vl).
  // The Scheduler page gives each sport its own tab (SCHEDULER_ROLE_PAGE.md), so
  // the in-grid sport picker is redundant there and is hidden. Unlocked (no prop)
  // the panel keeps its own picker for every other caller.
  const { isDark } = useTheme();
  // ``mode`` decides which API surface this panel talks to:
  //   * 'game'     — /api/game-schedule/*       (writes to game_slots, publishes WP+GCal)
  //   * 'practice' — /api/practice-schedule/*   (writes to practice_slots, publishes GCal only)
  // The whole rendering pipeline is shared — practices have no
  // opponent column so the UI just hides that one cell.
  const isPractice = mode === 'practice';
  // A season earlier than the sport's current one. Seasons are 'YYYY-YYYY', so a
  // plain string compare orders them correctly. Past seasons are readable but must
  // not be pushed to WordPress — WP carries the CURRENT season only.
  const apiBase = isPractice ? 'practice-schedule' : 'game-schedule';
  const [homeGames, setHomeGames] = useState([]);
  const [awayGames, setAwayGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [teamCounts, setTeamCounts] = useState({});
  const [aggTotals, setAggTotals] = useState({ home: 0, away: 0, total: 0 });
  const [practiceEvents, setPracticeEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [viewMode, setViewMode] = useState(initialView); // 'list' | 'calendar' — default to spreadsheet grid (matches Google Sheet UX)
  // 'games' = Regular Season Games, 'events' = Post Season & Events (slot_kind), 'practice', 'home', 'away'
  const [filter, setFilter] = useState('all');
  const [copying, setCopying] = useState(false);
  const [teamFilter, setTeamFilter] = useState('all'); // 'all' or a specific team slug
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [, setTeamSlugs] = useState([]);   // org team list — NOT used for columns (holds opponents too)
  const [teamColumns, setTeamColumns] = useState([]);
  // Tournaments our teams attend (TEAM_TOURNAMENTS.md): Calendar Week -> By team + List collapse.
  const [tournaments, setTournaments] = useState([]);
  const [openTourn, setOpenTourn] = useState({});
  const [calJump, setCalJump] = useState(null);
  const [listEdit, setListEdit] = useState(null);   // one team's game, opened from a List team cell
  const [seasonError, setSeasonError] = useState('');
  const [teamLabels, setTeamLabels] = useState({});
  const [dataSource, setDataSource] = useState('local');
  const [seasons, setSeasons] = useState([]);
  // Seasons that actually HAVE games, from the SOT (`GET /seasons?sport=`). Kept
  // separate from `seasons` because the dropdown is current + next + these.
  const [sotSeasons, setSotSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const isPastSeason = !!(currentSeason && selectedSeason && selectedSeason < currentSeason);
  const [opponents, setOpponents] = useState([]);
  // "+ Add New" opponent / venue popups (OPPONENTS_UI.md). `then` receives the new name.
  const [addTeam, setAddTeam] = useState(null);
  const [addVenue, setAddVenue] = useState(null);
  const [venues, setVenues] = useState([]);
  // A follow-up: home-venue prefill data ({ own_home_venue, opponents:{name→venue} }).
  const [homeVenues, setHomeVenues] = useState({ own_home_venue: '', opponents: {} });
  const [publishing, setPublishing] = useState(false);
  const [quickAddDate, setQuickAddDate] = useState(null);
  const [publishResult, setPublishResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [refConfigOpen, setRefConfigOpen] = useState(false);
  const [refConfig, setRefConfig] = useState({ agency_name: '', contact_name: '', email: '', phone: '', sms_enabled: false, email_enabled: true });
  // Schedule Settings ▸ Referee Agency is per sport (AJ 2026-10-20): refConfig = the selected
  // sport's agency, else the org-wide one.
  const [refSettings, setRefSettings] = useState(null);

  // v5.0 — Sport awareness + schedule settings
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState(() => {
    if (lockedSport) return lockedSport;
    try { return localStorage.getItem('ol.schedule.sport') || 'bk'; } catch { return 'bk'; }
  });
  const [newSportOpen, setNewSportOpen] = useState(false);
  const [newSportLabel, setNewSportLabel] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('referee'); // 'referee' | 'gcal' | 'durations'
  const [pubMenuOpen, setPubMenuOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [queueStatus, setQueueStatus] = useState({ total: 0, queued_await_oauth: 0, succeeded: 0, failed: 0 });
  // v5.1 — preview dialog before confirming a publish/unpublish
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewMode, setPreviewMode] = useState('publish'); // 'publish'|'unpublish'|'changed'

  // Column resize state
  const [colWidths, setColWidths] = useState({});
  const resizeRef = useRef(null);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    // Walk up to the <th> to get its width
    let th = e.target;
    while (th && th.tagName !== 'TH') th = th.parentElement;
    const startWidth = colWidths[colKey] || (th ? th.offsetWidth : 100);

    const onMouseMove = (ev) => {
      const delta = ev.clientX - startX;
      const newWidth = Math.max(40, startWidth + delta);
      setColWidths(prev => ({ ...prev, [colKey]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths]);

  const [form, setForm] = useState({
    date: '', opponent: '', home_away: 'home', location: '', location_address: '',
    games: {}, scheduling_status: 'pending', notes: '', team_confirmed: false, refs_confirmed: false, season: '2025-2026',
  });

  const bg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const inputCls = isDark ? 'bg-slate-700 border-slate-600 text-white' : '';
  const rowHover = isDark ? 'hover:bg-slate-700/40' : 'hover:bg-blue-50/50';

  const showMsg = (t, type = 'success') => { setMsg({ text: t, type }); setTimeout(() => setMsg({ text: '', type: '' }), 4000); };

  // ONE canonical level label everywhere (e.g. MSB-D, never MSBD/MS BOYS DEV).
  // Variant-aware: a slug variant like 'jvb-white' -> 'JVB-W' (never collapses
  // into JVB).
  const canonLevelLabel = (slug) => {
    if (!slug) return 'Unassigned';
    const k = String(slug).toLowerCase();
    const BASE = { vb: 'VB', vg: 'VG', jvb: 'JVB', jvg: 'JVG', msb: 'MSB', msg: 'MSG', 'msb-d': 'MSB-D' };
    if (BASE[k]) return BASE[k];
    const m = k.match(/^(vb|vg|jvb|jvg|msb|msg|msb-d|msbd)-(.+)$/);
    if (m) { const b = m[1] === 'msbd' ? 'msb-d' : m[1]; return (BASE[b] || b.toUpperCase()) + '-' + m[2][0].toUpperCase(); }
    return teamLabels[slug] || k.toUpperCase();
  };
  // Team COLUMNS / pills / filter ALL derive from the SAME authoritative set the
  // backend returns (published team set incl 0-game levels like JV Girls, plus
  // slug variants), so columns and counters can never diverge and no fixed level
  // list exists anywhere. Falls back to team_counts keys if the field is absent.
  // Never fall back to the org's whole team list — it also holds opponents and events
  // (a blank next season showed "Breakout Academy", "Ignite Flames" as columns). The
  // backend carries the latest season's squads into an empty season instead.
  const teamCols = (teamColumns && teamColumns.length)
    ? teamColumns.map(c => c.slug)
    : Object.keys(teamCounts || {});
  const colLabelMap = {};
  (teamColumns || []).forEach(c => { if (c && c.slug) colLabelMap[c.slug] = c.label; });
  const labelFor = (slug) => colLabelMap[slug] || canonLevelLabel(slug);
  // Badge totals are summed OVER THE SAME per-team map that renders the columns
  // and pills, so the badge, the pills and the grid can never disagree.
  const teamGameHome = teamCols.reduce((n, s) => n + ((teamCounts[s] && teamCounts[s].home) || 0), 0);
  const teamGameAway = teamCols.reduce((n, s) => n + ((teamCounts[s] && teamCounts[s].away) || 0), 0);
  const teamGameTotal = teamGameHome + teamGameAway;

  const fetchSchedule = useCallback(async () => {
    if (!selectedSeason) { setLoading(false); return; }
    try {
      const [schedRes, teamRes, seasonsRes, oppRes, venRes, practRes, sportsRes, hvRes] = await Promise.all([
        axios.get(`${API_URL}/api/${apiBase}/${orgId}`, { params: { season: selectedSeason, sport: selectedSport } }),
        axios.get(`${API_URL}/api/data-provider/${orgId}/teams`, { params: { sport: selectedSport, season: selectedSeason } }).catch(() => null),
        axios.get(`${API_URL}/api/${apiBase}/${orgId}/seasons`, { params: { sport: selectedSport } }).catch(() => null),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/opponents`).catch(() => null),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/venues`).catch(() => null),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/practice-events`, { params: { season: selectedSeason } }).catch(() => null),
        axios.get(`${API_URL}/api/${apiBase}/${orgId}/sports`, { params: { season: selectedSeason } }).catch(() => null),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/home-venues`, { params: { sport: selectedSport } }).catch(() => null),
      ]);
      // Practice mode: response shape is { practices: [...], total }.
      // Map to home_games/away_games slots so the rest of the
      // rendering pipeline doesn't need to branch — practices are
      // always treated as "home" (no away concept).
      let h, a;
      if (isPractice) {
        h = (schedRes.data.practices || []);
        a = [];
      } else {
        h = schedRes.data.home_games || [];
        a = schedRes.data.away_games || [];
      }
      setHomeGames(h);
      setAwayGames(a);
      setAllGames([...h, ...a].sort((x, y) => (x.date || '').localeCompare(y.date || '')));
      setTeamCounts(schedRes.data.team_counts || {});
      setTeamColumns(schedRes.data.team_columns || []);
      setTournaments(schedRes.data.tournaments || []);
      setAggTotals({
        home: schedRes.data.agg_home || h.length,
        away: schedRes.data.agg_away || a.length,
        total: schedRes.data.agg_total || (h.length + a.length),
        // Slots badge = the EXACT row set the grid renders (home_games ∪ away_games),
        // so the badge and the grid can never diverge — including non-competition rows
        // that the backend's competition-only `total` omits. (migration_log_0092)
        slots: h.length + a.length,
      });
      // Report season window (first/last event) to parent
      if (onSeasonWindow) {
        onSeasonWindow({ first: schedRes.data.first_date || '', last: schedRes.data.last_date || '' });
      }
      if (teamRes?.data) {
        setTeamSlugs(teamRes.data.team_slugs || []);
        setTeamLabels(teamRes.data.team_labels || {});
        setDataSource(teamRes.data.source || 'local');
      }
      // Seasons that HAVE games (SOT). The dropdown is current + next + these,
      // so a historical season can be opened and reviewed.
      if (seasonsRes?.data) setSotSeasons(seasonsRes.data.seasons || []);
      if (oppRes?.data) setOpponents(oppRes.data.opponents || []);
      if (hvRes?.data) setHomeVenues({ own_home_venue: hvRes.data.own_home_venue || '', opponents: hvRes.data.opponents || {} });
      if (venRes?.data) {
        // Prefer clean venue NAMES from the catalog; fall back to the mixed list.
        const full = (venRes.data.venues_full || []).map(v => v.name).filter(Boolean);
        const merged = Array.from(new Set([...(full), ...((venRes.data.venues) || [])]))
          .sort((a, b) => a.localeCompare(b));
        setVenues(merged);
      }
      if (practRes?.data) setPracticeEvents(practRes.data.events || []);
      if (sportsRes?.data) setSports(sportsRes.data.sports || []);
    } catch (err) { console.error('Failed to load schedule:', err); }
    finally { setLoading(false); }
  }, [orgId, selectedSeason, selectedSport, apiBase, isPractice]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Follow the pinned sport if the parent switches tabs.
  useEffect(() => {
    if (lockedSport && lockedSport !== selectedSport) setSelectedSport(lockedSport);
  }, [lockedSport]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist sport selection per-user — only when the picker is actually in use.
  // A pinned sport must never overwrite the user's own last choice.
  useEffect(() => {
    if (lockedSport) return;
    try { localStorage.setItem('ol.schedule.sport', selectedSport); } catch {}
  }, [selectedSport, lockedSport]);

  // Self-heal the sport picker: if the current selection isn't one of the
  // available sport slugs (e.g. a stale localStorage code from before the
  // slug alignment, or a sport that no longer exists), snap to the first
  // available sport so the dropdown always reflects a real, fetchable value.
  useEffect(() => {
    if (lockedSport) return;  // pinned by the parent tab — never snap away
    if (!sports || sports.length === 0) return;
    if (!sports.some(s => s.slug === selectedSport)) {
      setSelectedSport(sports[0].slug);
    }
  }, [sports, selectedSport, lockedSport]);

  // Load sports on mount INDEPENDENTLY of the season guard, so the current-season
  // default can resolve even before a season is selected (fetchSchedule early-
  // returns while selectedSeason is empty).
  useEffect(() => {
    axios.get(`${API_URL}/api/${apiBase}/${orgId}/sports`)
      .then(({ data }) => { if (data && data.sports) setSports(data.sports); })
      .catch(() => {});
  }, [orgId, apiBase]);

  // Season selector = ONLY the current season and current+1, from the selected
  // sport's current-season pin (never a literal year). Default = current season;
  // re-applies when the sport changes. This is the single source that also
  // replaces the old hardcoded season default.
  const lastAutoSportRef = useRef(null);
  const publishInflightRef = useRef(new Set());
  useEffect(() => {
    if (!sports || sports.length === 0) return;
    const sp = sports.find(s => s.slug === selectedSport);
    const y = sp && Number(sp.current_season_year) > 0 ? Number(sp.current_season_year) : null;
    if (!y) {
      // Never silently show 0 games — surface the resolution failure instead.
      setSeasonError(`Couldn't resolve the current season for "${selectedSport}". Check that sport's season settings.`);
      return;
    }
    setSeasonError('');
    const cur = `${y}-${y + 1}`;
    const next = `${y + 1}-${y + 2}`;
    // Current + next (always offered, even with no games yet) plus every season
    // the SOT actually has games for, newest first. Historical seasons used to be
    // unreachable here, which made a past season impossible to review.
    const merged = Array.from(new Set([cur, next, ...sotSeasons]))
      .filter(Boolean)
      .sort((a, b) => b.localeCompare(a));
    setSeasons(merged);
    setCurrentSeason(cur);
    if (lastAutoSportRef.current !== selectedSport) {
      lastAutoSportRef.current = selectedSport;
      setSelectedSeason(cur);
    } else if (selectedSeason && !merged.includes(selectedSeason)) {
      // Only snap back when the selection is not on the list at all. A PAST
      // season is a legitimate choice now, so it must not be snapped away.
      setSelectedSeason(cur);
    }
  }, [sports, selectedSport, sotSeasons]); // eslint-disable-line react-hooks/exhaustive-deps

  // Referee agency + the Google Calendar publish-queue pill. The full settings
  // payload (per-sport calendars, durations, OAuth config) is loaded by
  // ScheduleSettingsDialog when it opens — only `refConfig` is needed out here,
  // because the publish flow reads it to decide who gets the game notice.
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API_URL}/api/game-schedule/${orgId}/settings`).then(({ data }) => {
      if (data) setRefSettings({ referee: data.referee || {}, referee_by_sport: data.referee_by_sport || {} });
    }).catch(() => { /* first-load empty is fine */ });

    // Poll the Google Calendar publish queue status so the UI can show a pill.
    axios.get(`${API_URL}/api/game-schedule/${orgId}/publish-queue`, { params: { sport: selectedSport } })
      .then(({ data }) => setQueueStatus(data || {}))
      .catch(() => {});
  }, [orgId, selectedSport]);

  useEffect(() => {
    if (!refSettings) return;
    const r = (refSettings.referee_by_sport || {})[selectedSport] || refSettings.referee || {};
    setRefConfig({ agency_name: '', contact_name: '', email: '', phone: '', sms_enabled: false, email_enabled: false, ...r });
  }, [refSettings, selectedSport]);

  /* The Google-Calendar handlers and saveSettings moved into
     ScheduleSettingsDialog with the dialog itself (2026-10-08). */

  const addSport = async () => {
    const label = (newSportLabel || '').trim();
    if (!label) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/game-schedule/${orgId}/sports`, { label });
      setSports(prev => {
        const exists = prev.some(s => s.slug === data.slug);
        return exists ? prev : [...prev, data].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      });
      setSelectedSport(data.slug);
      setNewSportOpen(false);
      setNewSportLabel('');
      showMsg(`Sport "${data.label}" added`);
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Add failed', 'error');
    }
  };

  const openPublishPreview = async (mode) => {
    if (readOnly) return;
    setPubMenuOpen(false);
    setPreviewMode(mode);
    setPreviewData(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/game-schedule/${orgId}/publish-preview`, {
        mode, season: selectedSeason, sport: selectedSport, include_practices: true,
      });
      setPreviewData(data);
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Preview failed', 'error');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmPublish = async () => {
    if (!previewData) return;
    const mode = previewMode;
    setPublishingAll(true);
    setPreviewOpen(false);
    try {
      const endpoint = mode === 'unpublish' ? 'unpublish-all' : 'publish-all';
      const body = { season: selectedSeason, sport: selectedSport, include_practices: true };
      if (mode === 'changed') body.changed_only = true;
      const { data } = await axios.post(`${API_URL}/api/game-schedule/${orgId}/${endpoint}`, body);
      if (mode === 'unpublish') {
        showMsg(`Unpublished ${data.wp_removed} games (${data.wp_failed} failed). Practice gcal deletes queued: ${data.gcal_delete_queued_practices}.`,
          data.wp_failed ? 'error' : 'success');
      } else {
        showMsg(`WP pushed ${data.wp_published} games (${data.wp_failed} failed). Google Calendar queued: ${data.gcal_queued_games} games + ${data.gcal_queued_practices} practices.`,
          data.wp_failed ? 'error' : 'success');
      }
      fetchSchedule();
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed', 'error');
    } finally {
      setPublishingAll(false);
    }
  };

  /* The manual "Refresh sync status" button was removed (2026-10-08): the 60s
     background drift auditor runs the SAME /api/sync-drift/{gcal,wp} checks and the
     W/G pills self-heal from it, so the button only duplicated work the page was
     already doing. The Scheduler's cache escape hatch is Reset Cache on the hub's
     common row. See SCHEDULER_ROLE_PAGE.md. */

  const togglePublishSlot = async (slot) => {
    if (readOnly) return;
    // Games publish to BOTH WP and Google Calendar in lockstep. Practice
    // rows (future work) hit a GCal-only endpoint. The icon reflects
    // whichever sync state the row is in.
    //
    // Predicate matches the backend's `is_published` check
    // (routes/scheduler_sport.publish_single_slot). We treat residual
    // WP/GCal ids as evidence of publication even when the booleans
    // got cleared — e.g. the URL-change smart-diff resets `wp_pushed`
    // but leaves `wp_game_id` so the AD can still tear it down.
    const currentlyPublished = !!(
      slot.wp_pushed || slot.gcal_pushed || slot.wp_game_id || slot.gcal_event_id
    );
    const verb = currentlyPublished ? 'Unpublish' : 'Publish';
    const detail = currentlyPublished
      ? 'Removes from WordPress AND Google Calendar.'
      : 'Pushes to WordPress AND Google Calendar.';
    if (!window.confirm(`${verb} this game?\n\n${slot.date} · ${slot.opponent || 'TBD'}\n\n${detail}`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/${apiBase}/${orgId}/publish-slot/${slot.id}`);
      showMsg(
        data.action === 'unpublished' ? 'Game unpublished (WP + GCal)' : 'Game published (WP + GCal)',
        'success',
      );
      // Merge the fresh slot (carries the post-drain wp_pushed + gcal_pushed
      // flags) so both dots update instantly without a full refetch.
      if (data.slot) {
        const f = data.slot;
        const merge = (arr) => arr.map(s => (s.id === slot.id ? {
          ...s, ...f,
          wp_pushed: !!f.wp_pushed,
          gcal_pushed: !!f.gcal_pushed,
          wp_game_id: f.wp_game_id ?? null,
          gcal_event_id: f.gcal_event_id ?? null,
          gcal_event_ids: f.gcal_event_ids ?? [],
        } : s));
        setHomeGames(prev => merge(prev));
        setAwayGames(prev => merge(prev));
        setAllGames(prev => merge(prev));
      } else {
        fetchSchedule();
      }
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Publish toggle failed', 'error');
    }
  };

  // Independent per-destination publish toggle (game rows only). Clicking the
  // W badge touches WordPress ONLY; clicking G touches Google Calendar ONLY —
  // wired to the backend `?dest=wp|gcal` control on publish-slot. AJ's model:
  // this is a plain push-to-publish toggle, NO friction on the normal path
  // (publish, or unpublish a not-yet-final game). The ONE exception is
  // UN-publishing a game that is already FINAL — that pulls played-game content
  // off the live public website/calendar, the only genuinely costly misclick —
  // so we confirm and name the destination. (Deliberate choice, see TEAM_SCHEDULER.md.)
  // ONE shared predicate for both the click handler (isOn) and the badge, so
  // they can never disagree about publish state (defect 2c).
  const pubOn = (slot, dest) => dest === 'wp'
    ? !!(slot.wp_pushed || slot.wp_game_id)
    : !!(slot.gcal_pushed || slot.gcal_event_id || (slot.gcal_event_ids && slot.gcal_event_ids.length));

  const togglePublishDest = async (slot, dest) => {
    if (readOnly) return;
    // Idempotency guard: ignore a second click for the same slot+dest while the
    // first request is in flight (a double-click used to fire draft-then-publish
    // ~1s apart and flip state back — migration_log_0173).
    const inflightKey = `${slot.id}:${dest}`;
    if (publishInflightRef.current.has(inflightKey)) return;
    publishInflightRef.current.add(inflightKey);
    const isOn = pubOn(slot, dest);
    const label = dest === 'wp' ? 'WordPress' : 'Google Calendar';
    const isFinal = (slot.game_details || slot.games || []).some(g => g.is_final);
    if (isOn && isFinal) {
      const target = dest === 'wp' ? 'the website' : 'Google Calendar';
      if (!window.confirm(`Remove this final game from ${target}?\n\n${slot.date} · ${slot.opponent || 'TBD'}\n\nThis pulls a played game off the live ${target}.`)) return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/${apiBase}/${orgId}/publish-slot/${slot.id}`, null, { params: { dest } });
      const off = /unpublished|unsynced/.test(data.action || '');
      showMsg(`${label} ${off ? 'removed' : 'published'}`, 'success');
      if (data.slot) {
        const f = data.slot;
        const merge = (arr) => arr.map(s => (s.id === slot.id ? {
          ...s, ...f,
          wp_pushed: !!f.wp_pushed,
          gcal_pushed: !!f.gcal_pushed,
          wp_game_id: f.wp_game_id ?? null,
          gcal_event_id: f.gcal_event_id ?? null,
          gcal_event_ids: f.gcal_event_ids ?? [],
        } : s));
        setHomeGames(prev => merge(prev));
        setAwayGames(prev => merge(prev));
        setAllGames(prev => merge(prev));
      } else {
        fetchSchedule();
      }
    } catch (err) {
      showMsg(err.response?.data?.detail || `${label} toggle failed`, 'error');
    } finally {
      publishInflightRef.current.delete(inflightKey);
    }
  };

  const notifyRefsOne = async (slot) => {
    if (readOnly) return;
    if (!window.confirm(`Send referee notice for this game?\n\n${slot.date} · ${slot.opponent || 'TBD'}`)) return;
    try {
      await axios.post(`${API_URL}/api/game-schedule/${orgId}/ref-notify/${slot.id}`);
      showMsg('Referee agency notified', 'success');
      fetchSchedule();
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Ref notification failed', 'error');
    }
  };

  const saveRefConfig = async () => {
    try {
      await axios.post(`${API_URL}/api/game-schedule/${orgId}/ref-config`, refConfig);
      showMsg('Referee contact saved', 'success');
      setRefConfigOpen(false);
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Save failed', 'error');
    }
  };

  // A follow-up: default venue for a game — HOME → own squad home venue,
  // AWAY → the opponent's home venue (when the opponent is a known team).
  // The value stays editable; callers only auto-fill when the field is empty.
  const venueFor = (homeAway, opponent) => {
    if (isPractice) return '';
    if (homeAway === 'home') return homeVenues.own_home_venue || '';
    if (homeAway === 'away' && opponent) {
      return (homeVenues.opponents || {})[opponent.trim().toLowerCase()] || '';
    }
    return '';
  };

  const openAdd = (homeAway, date = '') => {
    if (readOnly) return;
    const ha = homeAway || 'home';
    setForm({
      date: date || '', last_date: '', opponent: '', home_away: ha, location: venueFor(ha, ''), location_address: '',
      games: {}, scheduling_status: 'pending', notes: '', team_confirmed: false, refs_confirmed: false,
      season: selectedSeason, sport: selectedSport, slot_kind: filter === 'events' ? 'event' : 'game',
      _loc_auto: true,   // Location came from the home-venue prefill, not the user
    });
    setEditSlot(null);
    setShowAddDialog(true);
  };

  const openEdit = (slot) => {
    if (readOnly) return;
    const gamesMap = {};
    (slot.games || []).forEach(g => { gamesMap[g.team_slug] = g.time; });
    setForm({
      date: slot.date || '', opponent: slot.opponent || '', home_away: slot.home_away || 'home',
      location: slot.location || '', location_address: slot.location_address || '',
      games: gamesMap,
      // Any legacy status (tbd/completed) is normalized to pending/confirmed at the API layer,
      // but defensively clamp here too so the dialog's select never shows a missing option.
      scheduling_status: slot.scheduling_status === 'confirmed' ? 'confirmed' : 'pending',
      notes: slot.notes || '', team_confirmed: slot.team_confirmed || false,
      refs_confirmed: slot.refs_confirmed || false, season: slot.season || selectedSeason,
      sport: slot.sport || selectedSport, slot_kind: slot.slot_kind === 'event' ? 'event' : 'game',
    });
    setEditSlot(slot);
    setShowAddDialog(true);
  };

  const saveSlot = async () => {
    setSaving(true);
    const gamesArr = Object.entries(form.games)
      .filter(([, time]) => time && time.trim())
      .map(([slug, time]) => ({ team_slug: slug, time }));

    // Build payload — strip game-only fields in practice mode so the
    // backend whitelist doesn't have to silently drop them and the
    // wire shape stays clean.
    let payload;
    if (isPractice) {
      payload = {
        date: form.date,
        day_of_week: getDayOfWeek(form.date),
        location: form.location,
        notes: form.notes,
        season: form.season,
        sport: form.sport,
        scheduling_status: form.scheduling_status,
        team_confirmed: !!form.team_confirmed,
        games: gamesArr,
      };
    } else {
      const { _loc_auto, last_date, ...rest } = form;   // UI-only fields never go to the API
      payload = {
        ...rest,
        day_of_week: getDayOfWeek(form.date),
        games: gamesArr,
      };
    }
    // Multi-day event (Add only): the same row on every day from Date through Last day.
    const days = [];
    if (!editSlot && !isPractice && form.last_date && form.date && form.last_date > form.date) {
      for (let d = new Date(`${form.date}T12:00:00`); ; d.setDate(d.getDate() + 1)) {
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (iso > form.last_date) break;
        days.push(iso);
        if (days.length > 31) break;
      }
    }

    try {
      if (days.length > 1) {
        for (const iso of days) {
          await axios.post(`${API_URL}/api/${apiBase}/${orgId}`, { ...payload, date: iso, day_of_week: getDayOfWeek(iso) });
        }
        showMsg(`${days.length} days added (${days[0]} – ${days[days.length - 1]})`);
      } else if (editSlot) {
        // slot_kind rides in the PUT (canonical rows are rebuilt with it, marked as the user's choice)
        await axios.put(`${API_URL}/api/${apiBase}/${orgId}/${editSlot.id}`, payload);
        showMsg(`${isPractice ? 'Practice' : 'Game'} slot updated`);
      } else {
        await axios.post(`${API_URL}/api/${apiBase}/${orgId}`, payload);
        showMsg(`${isPractice ? 'Practice' : 'Game'} slot created`);
      }
      setShowAddDialog(false);
      fetchSchedule();
    } catch (err) { showMsg(err.response?.data?.detail || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  // New season: carry the Post Season & Events list over from the previous season
  // (same weekday, +52 weeks; editable). With no events last season, the org's
  // imported default list (Optional Dates baseline) is used. Never published.
  const prevSeasonOf = (s) => { const y = parseInt((s || '').slice(0, 4), 10); return y ? `${y - 1}-${y}` : ''; };
  const keyFor = (k) => {
    const norm = (x) => (x || '').toLowerCase().replace(/-/g, '');
    return teamCols.find(c => c === k) || teamCols.find(c => norm(c) === norm(k)) || null;
  };
  const copyEvents = async () => {
    const from = prevSeasonOf(selectedSeason);
    if (!from || !window.confirm(`Add ${from}'s Post Season & Events to ${selectedSeason}? Dates move forward one year (same weekday) — edit any that differ.`)) return;
    setCopying(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/game-schedule/${orgId}`, { params: { season: from, sport: selectedSport } });
      let src = [...(data.home_games || []), ...(data.away_games || [])].filter(g => g.slot_kind === 'event')
        .map(g => {
          const d = new Date(`${g.date}T12:00:00`); d.setDate(d.getDate() + 364);
          return { date: d.toISOString().slice(0, 10), opponent: g.opponent, home_away: g.home_away,
            location: g.location, games: g.games || [] };
        });
      if (!src.length) {
        const base = await axios.get(`${API_URL}/api/scheduler/${orgId}/optional-dates`, { params: { sport: selectedSport } }).catch(() => null);
        const y0 = parseInt(selectedSeason.slice(0, 4), 10);
        const startMonth = base?.data?.school_year_start_month || 8;   // org setting (Settings ▸ school year)
        src = [];
        ((base?.data?.events) || []).forEach(ev => (ev.dates || []).forEach(dt => {
          const yr = dt.month >= startMonth ? y0 : y0 + 1;
          src.push({ date: `${yr}-${String(dt.month).padStart(2, '0')}-${String(dt.day).padStart(2, '0')}`,
            opponent: ev.opponent || ev.event_name, home_away: ev.is_home ? 'home' : 'away', location: '',
            games: (ev.levels || []).map(l => ({ team_slug: l, time: dt.time || '' })) });
        }));
      }
      let made = 0;
      for (const e of src) {
        const games = e.games.map(t => ({ team_slug: keyFor(t.team_slug), time: t.time })).filter(t => t.team_slug && t.time);
        if (!games.length) continue;
        await axios.post(`${API_URL}/api/game-schedule/${orgId}`, {
          date: e.date, day_of_week: getDayOfWeek(e.date), opponent: e.opponent, home_away: e.home_away,
          location: e.location, games, season: selectedSeason, sport: selectedSport,
          scheduling_status: 'pending', slot_kind: 'event' });
        made += 1;
      }
      showMsg(made ? `Added ${made} Post Season & Events rows — review the dates` : `Nothing to copy from ${from}`);
      fetchSchedule();
    } catch (err) { showMsg(err.response?.data?.detail || 'Copy failed', 'error'); }
    finally { setCopying(false); }
  };

  // Post Season & Events ▸ Event column: game vs non-game event (website/app prefix + game totals)
  const setEventFlag = async (slot, isEvent) => {
    try {
      await axios.put(`${API_URL}/api/game-schedule/${orgId}/${slot.id}/event`, { is_event: isEvent });
      fetchSchedule();
    } catch (err) { showMsg(err.response?.data?.detail || 'Could not save', 'error'); }
  };

  // Trash on a collapsed tournament row: every game of that tournament (all teams, all days),
  // each through the per-game delete (delete_game_everywhere — post off the website too).
  const deleteTournamentGames = async (t) => {
    if (readOnly) return;
    const ids = [...new Set(allGames.flatMap(sl => (sl.games || [])
      .filter(g => g.tournament_id === t.id && g.game_id).map(g => g.game_id)))];
    if (!ids.length || !window.confirm(`Delete all ${ids.length} games of ${t.name} (every team, every day)? Any that are on the website come off it.`)) return;
    let failed = 0;
    for (const id of ids) {
      try { await axios.delete(`${API_URL}/api/game-schedule/${orgId}/games/${id}`); } catch { failed += 1; }
    }
    showMsg(failed ? `${ids.length - failed} deleted, ${failed} failed` : `${ids.length} games deleted`, failed ? 'error' : undefined);
    fetchSchedule();
  };

  const deleteSlot = async (slotId) => {
    if (readOnly) return;
    try {
      await axios.delete(`${API_URL}/api/${apiBase}/${orgId}/${slotId}`);
      showMsg(`${isPractice ? 'Practice' : 'Game'} slot deleted`);
      fetchSchedule();
    } catch (err) { showMsg('Delete failed', 'error'); }
  };

  const toggleConfirm = async (slot, field) => {
    if (readOnly) return;
    try {
      // Practices use PATCH (no /confirm endpoint — just inline-patch
      // the boolean). Games keep their existing /confirm endpoint
      // which has the side-effect of resetting wp_pushed.
      if (isPractice) {
        await axios.patch(`${API_URL}/api/${apiBase}/${orgId}/${slot.id}`, { [field]: !slot[field] });
      } else {
        await axios.put(`${API_URL}/api/game-schedule/${orgId}/${slot.id}/confirm`, { [field]: !slot[field] });
      }
      fetchSchedule();
    } catch {}
  };

  const updateStatus = async (slot, newStatus) => {
    if (readOnly) return;
    try {
      if (isPractice) {
        await axios.patch(`${API_URL}/api/${apiBase}/${orgId}/${slot.id}`, { scheduling_status: newStatus });
      } else {
        await axios.put(`${API_URL}/api/game-schedule/${orgId}/${slot.id}/confirm`, { scheduling_status: newStatus });
      }
      fetchSchedule();
    } catch {}
  };

  // Inline-edit one cell. Issues a PATCH against the schedule
  // endpoint (game- or practice-) with a single-field body.
  // Optimistically updates local state so the cell reflects
  // the new value before the round-trip lands; a quiet refetch picks
  // up any server-side normalisation (day_of_week recomputation,
  // wp_pushed reset, etc.). Throws on failure so the cell component
  // can flag itself red.
  const patchSlotField = useCallback(async (slotId, field, newValue) => {
    if (readOnly) return;
    // Optimistic local merge — we mutate both home + away lists
    // because a single slot only lives in one of them, but it's
    // cheaper to map both than to figure out which.
    const merge = (list) => list.map(s => s.id === slotId ? { ...s, [field]: newValue } : s);
    setHomeGames(prev => merge(prev));
    setAwayGames(prev => merge(prev));
    try {
      await axios.patch(`${API_URL}/api/${apiBase}/${orgId}/${slotId}`, { [field]: newValue });
    } catch (err) {
      // Revert by refetching truth from server, so the optimistic
      // value doesn't linger after a rejection.
      fetchSchedule();
      throw err;
    }
    // Quiet refetch — picks up server-derived fields (day_of_week,
    // wp_pushed reset). No spinner, no UI flash.
    try { fetchSchedule(); } catch {}
  }, [orgId, fetchSchedule, apiBase]);

  // Several fields in one save (the Notes popup: notes + Google Calendar note).
  const patchSlotFields = useCallback(async (slotId, fields) => {
    if (readOnly || !fields || !Object.keys(fields).length) return;
    const merge = (list) => list.map(s => s.id === slotId ? { ...s, ...fields } : s);
    setHomeGames(prev => merge(prev));
    setAwayGames(prev => merge(prev));
    try {
      await axios.patch(`${API_URL}/api/${apiBase}/${orgId}/${slotId}`, fields);
    } catch (err) {
      fetchSchedule();
      throw err;
    }
    try { fetchSchedule(); } catch {}
  }, [orgId, fetchSchedule, apiBase, readOnly]);

  // Inline-edit a single team's kickoff time directly from the table
  // (no row-expand required). Wraps the existing PUT /team-time
  // endpoint with the same optimistic-update + silent-refetch dance.
  // Time strings are stored 12h ("6:00 PM"); the input is a native
  // <input type="time"> which produces 24h ("18:00") — we convert
  // here so the wire format never sees 24h.
  const patchTeamTime = useCallback(async (slotId, teamSlug, hhmm) => {
    if (readOnly) return;
    const wireTime = hhmm ? to12h(hhmm) : '';
    const merge = (list) => list.map(s => {
      if (s.id !== slotId) return s;
      const games = (s.games || []).filter(g => g.team_slug !== teamSlug);
      if (wireTime) games.push({ team_slug: teamSlug, time: wireTime });
      return { ...s, games };
    });
    setHomeGames(prev => merge(prev));
    setAwayGames(prev => merge(prev));
    try {
      await axios.put(`${API_URL}/api/${apiBase}/${orgId}/${slotId}/team-time`,
        { team_slug: teamSlug, time: wireTime });
    } catch (err) {
      fetchSchedule();
      throw err;
    }
    try { fetchSchedule(); } catch {}
  }, [orgId, fetchSchedule, apiBase]);

  const publishCalendar = async () => {
    if (readOnly) return;
    if (!window.confirm(`Publish ${selectedSeason} calendar? This will notify all coaches and team members, and also send the referee agency ${(!refConfig.email && !refConfig.phone) ? '(NOT CONFIGURED)' : 'a game notice for any row not already sent'}.`)) return;
    setPublishing(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/game-schedule/${orgId}/publish`, { season: selectedSeason });
      showMsg(`Calendar published! ${data.slots_published} games. ${data.notifications_sent} notifications sent.`);
      setPublishResult(data);

      // Fire ref-agency notifications for every slot whose refs_notified flag is still false.
      // These are either brand-new games or games whose rows were edited since the last notify,
      // so the agency always receives only the change set.
      if ((refConfig.email_enabled && refConfig.email) || (refConfig.sms_enabled && refConfig.phone)) {
        const unnotified = allGames.filter(s => !s.refs_notified);
        let sent = 0, failed = 0;
        for (const s of unnotified) {
          try {
            await axios.post(`${API_URL}/api/game-schedule/${orgId}/ref-notify/${s.id}`);
            sent++;
          } catch {
            failed++;
          }
        }
        if (sent > 0 || failed > 0) {
          showMsg(`Referee agency: ${sent} notified${failed ? `, ${failed} failed` : ''}`, failed ? 'error' : 'success');
        }
      }

      fetchSchedule();
    } catch (err) { showMsg(err.response?.data?.detail || 'Publish failed', 'error'); }
    finally { setPublishing(false); }
  };

  const syncResults = async () => {
    if (readOnly) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/game-schedule/${orgId}/sync-results`);
      showMsg(`${data.completed} games marked completed, ${data.skipped} skipped (no scores)`);
      fetchSchedule();
    } catch (err) { showMsg(err.response?.data?.detail || 'Sync failed', 'error'); }
    finally { setSyncing(false); }
  };

  const handleCalendarDateClick = (dateStr, time) => {
    if (readOnly) return;
    openAdd('home', dateStr, time);
  };

  const handleCalendarEventClick = (source, type) => {
    if (type === 'game') {
      openEdit(source);
    }
  };

  // Drag-to-reschedule from the Calendar view. Writes through the normal edit
  // path: a date move via patchSlotField('date'), a time move via patchTeamTime
  // (24h "HH:MM" → stored 12h). Games only; never a Google write on its own.
  const handleCalendarReschedule = async (source, patch) => {
    if (readOnly) return;
    try {
      if (patch.date && patch.date !== source.date) {
        await patchSlotField(source.id, 'date', patch.date);
      }
      if (patch.time && patch.teamSlug) {
        await patchTeamTime(source.id, patch.teamSlug, patch.time);
      }
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Reschedule failed', 'error');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  const statusColor = (s) => {
    // Only two statuses exist: pending (yellow) and confirmed (green).
    if (s === 'confirmed') return 'bg-green-600';
    return 'bg-yellow-600';
  };

  // Clamp any legacy status (tbd/completed/etc.) to one of the two allowed
  // values for display purposes. This is defensive — the backend migration
  // already rewrote all legacy rows, but fresh MSTW syncs can reintroduce
  // weird values and we never want the Status column to show "completed".
  const displayStatus = (s) => (s === 'confirmed' ? 'confirmed' : 'pending');

  // Filter games for list view
  let listGames = filter === 'home' ? homeGames
    : filter === 'away' ? awayGames
    : allGames;
  // Regular Season Games vs Post Season & Events (SCHEDULER_ROLE_PAGE §Post Season & Events)
  const isEvent = (g) => g.slot_kind === 'event';
  if (filter === 'games') listGames = listGames.filter(g => !isEvent(g));
  if (filter === 'events') listGames = listGames.filter(isEvent);
  const calendarGames = filter === 'games' ? allGames.filter(g => !isEvent(g))
    : filter === 'events' ? allGames.filter(isEvent) : allGames;
  // Apply team filter to list view
  if (teamFilter !== 'all') {
    listGames = listGames.filter(g => (g.games || []).some(t => t.team_slug === teamFilter));
  }

  const publishedCount = allGames.filter(g => g.published).length;
  const unpublishedCount = allGames.length - publishedCount;

  return (
    <div className="space-y-4" data-testid="game-schedule-panel">
      {msg.text && (
        <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${msg.type === 'error' ? 'bg-red-500/10 border border-red-500/50 text-red-400' : 'bg-green-500/10 border border-green-500/50 text-green-400'}`}>
          {msg.type === 'error' ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}{msg.text}
        </div>
      )}

      {/* Post-Publish Google Calendar Banner */}
      {publishResult && (
        <div className={`rounded-lg p-4 border ${isDark ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Calendar className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                  Schedule Published — Add to Google Calendar
                </p>
                <p className={`text-xs ${isDark ? 'text-blue-400/70' : 'text-blue-600/70'}`}>
                  {publishResult.slots_published} games published. {publishResult.notifications_sent} notifications sent.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => window.open(publishResult.google_calendar_url, '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7" data-testid="gcal-subscribe-btn">
                <Calendar className="h-3 w-3 mr-1" /> Add to Google Calendar
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                navigator.clipboard.writeText(publishResult.ics_feed_url);
                showMsg('ICS feed URL copied to clipboard');
              }} className={`h-7 text-xs ${isDark ? 'border-blue-700 text-blue-300' : ''}`} data-testid="copy-ics-btn">
                ICS Feed
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPublishResult(null)}
                className="h-7 text-xs" data-testid="dismiss-publish-btn">
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {seasonError && (
        <div className="mb-2 p-3 rounded-lg border border-red-300 bg-red-50 text-red-700 text-sm" data-testid="season-resolve-error">
          {seasonError}
        </div>
      )}

      {isPastSeason && (
        <div className={`mb-2 p-2 rounded-lg border text-xs ${isDark
            ? 'border-amber-700/50 bg-amber-900/15 text-amber-300'
            : 'border-amber-300 bg-amber-50 text-amber-800'}`}
          data-testid="past-season-banner">
          Viewing the <strong>{selectedSeason}</strong> season — this is history (current is{' '}
          <strong>{currentSeason}</strong>). WordPress carries the current season only, so
          <strong> publishing is disabled</strong> here. Edits still write to the OL record.
        </div>
      )}

      {!isPractice && filter === 'events' && !readOnly && allGames.filter(g => g.slot_kind === 'event').length === 0 && (
        <div className={`rounded-lg border p-3 flex items-center gap-3 text-sm ${isDark ? 'border-slate-700 text-slate-300' : 'border-amber-200 bg-amber-50 text-slate-700'}`} data-testid="events-empty">
          No Post Season &amp; Events for {selectedSeason} yet.
          <Button size="sm" disabled={copying} onClick={copyEvents} className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid="events-copy-prev">
            {copying ? 'Copying…' : `Copy from ${prevSeasonOf(selectedSeason)}`}</Button>
        </div>
      )}
      {/* Top Control Bar — sticky so Sport, Publish, Add Game and Settings
          stay reachable as the user scrolls through the grid. */}
      <div className={`sticky top-0 z-30 flex items-center justify-between flex-wrap gap-2 p-3 rounded-lg border shadow-sm ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-amber-50 border-amber-200'}`}
        data-testid="schedule-summary">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-sm font-semibold ${text} inline-flex items-center gap-1`}>
            <Lock className="h-3.5 w-3.5 opacity-70" title="This is the locked default scheduler view" />
            {isPractice ? 'Practice Schedule' : 'Game Schedule'}
          </span>
          {dataSource === 'mstw' && !isPractice && (
            <Badge className="bg-emerald-600 text-white text-[10px]"><Globe className="h-3 w-3 mr-1" />MSTW Live</Badge>
          )}
          {!isPractice && (
            <Badge variant="outline" className="text-xs" title="Home team-games (one per team playing that date)"><Home className="h-3 w-3 mr-1" />{teamGameHome} Home</Badge>
          )}
          {!isPractice && (
            <Badge variant="outline" className="text-xs" title="Away team-games (one per team playing that date)"><Plane className="h-3 w-3 mr-1" />{teamGameAway} Away</Badge>
          )}
          {isPractice ? (
            <Badge variant="outline" className="text-xs" title="Practice events" data-testid="badge-slots"><CalendarClock className="h-3 w-3 mr-1" />{aggTotals.slots} Practices</Badge>
          ) : (
            <>
              <Badge variant="outline" className="text-xs" title="Slot rows on the grid — one per date + opponent (matches the TS Schedules list)" data-testid="badge-slots"><Calendar className="h-3 w-3 mr-1" />{aggTotals.slots} Slots</Badge>
              <Badge variant="outline" className="text-xs" title="Team-games — one per team playing, summed over the same columns shown (matches the TS Games count)" data-testid="badge-team-games"><Trophy className="h-3 w-3 mr-1" />{teamGameTotal} Team-Games</Badge>
            </>
          )}
          {unpublishedCount > 0 && (
            <Badge className="bg-amber-500/20 text-amber-400 text-[10px]"><EyeOff className="h-3 w-3 mr-1" />{unpublishedCount} Unpublished</Badge>
          )}
          {queueStatus.queued_await_oauth > 0 && (
            <Badge className="bg-blue-500/20 text-blue-400 text-[10px]" title="Google Calendar writes queued — waiting on OAuth">
              <Calendar className="h-3 w-3 mr-1" />{queueStatus.queued_await_oauth} GCal queued
            </Badge>
          )}
        </div>
        {!readOnly && (
        <div className="flex items-center gap-2">
          {/* Org-wide settings. When a parent pins the sport (the Scheduler hub) it
              renders this button in its COMMON row instead, so it is not repeated
              inside each sport's toolbar. */}
          {!lockedSport && (
          <Button size="sm" variant="outline" onClick={() => { setSettingsTab('referee'); setSettingsOpen(true); }}
            className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`} data-testid="schedule-settings-btn" title="Referees + Google Calendars for all sports">
            <SettingsIcon className="h-3 w-3 mr-1" />
            Settings
          </Button>
          )}
          {/* Was mislabelled "Sync Results" — it syncs nothing. It marks games with
              scores as completed (POST /sync-results returns completed/skipped).
              Hidden for the Scheduler (lockedSport) — AJ 2026-10-14. */}
          {!lockedSport && (
          <Button size="sm" variant="outline" onClick={syncResults} disabled={syncing}
            className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`} data-testid="sync-results-btn"
            title="Mark every game that already has a score as completed. Games without scores are skipped.">
            {syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
            Mark Played
          </Button>
          )}
          {/* Publish All dropdown */}
          <div className="relative">
            <Button size="sm" onClick={() => setPubMenuOpen(o => !o)} disabled={publishingAll || isPastSeason}
              title={isPastSeason ? 'WordPress carries the CURRENT season only — publishing a past season is blocked.' : undefined}
              className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="pub-menu-btn">
              {publishingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Publish
              <ChevronsUpDown className="h-3 w-3 opacity-70" />
            </Button>
            {pubMenuOpen && (
              <div className={`absolute right-0 mt-1 w-64 z-50 rounded-md border shadow-lg ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'}`}
                onMouseLeave={() => setPubMenuOpen(false)}
                data-testid="pub-menu">
                <button onClick={() => openPublishPreview('publish')}
                  className={`w-full text-left px-3 py-2 text-xs hover:${isDark ? 'bg-slate-700' : 'bg-slate-100'} ${text}`}
                  data-testid="pub-menu-publish-all">
                  <div className="font-semibold">Publish ALL unpublished</div>
                  <div className={`${muted} text-[10px]`}>WP + Google Calendar (games and practices)</div>
                </button>
                <button onClick={() => openPublishPreview('changed')}
                  className={`w-full text-left px-3 py-2 text-xs hover:${isDark ? 'bg-slate-700' : 'bg-slate-100'} ${text} border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                  data-testid="pub-menu-publish-changed">
                  <div className="font-semibold">Publish changed only</div>
                  <div className={`${muted} text-[10px]`}>Only rows edited since last publish</div>
                </button>
                <button onClick={() => openPublishPreview('unpublish')}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-red-500/10 text-red-500 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                  data-testid="pub-menu-unpublish-all">
                  <div className="font-semibold">Unpublish ALL published</div>
                  <div className={`opacity-70 text-[10px]`}>Remove from WP + Google Calendar</div>
                </button>
              </div>
            )}
          </div>
          {!isPractice && (
            <Button size="sm" variant="outline"
              onClick={() => setShowImport(true)}
              title="Import games from a Google Sheet or MSTW-style CSV straight into the OL schedule (same tool the Tech Specialist uses)."
              className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
              data-testid="scheduler-import-btn">
              <Database className="h-3 w-3 mr-1" />Import
            </Button>
          )}
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-7"
            onClick={() => openAdd(filter === 'away' ? 'away' : 'home')} data-testid="add-game-btn">
            <Plus className="h-4 w-4 mr-1" />Add {isPractice ? 'Practice' : 'Game'}
          </Button>
        </div>
        )}
      </div>

      {/* Team counts row */}
      {teamCols.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {teamCols.map(slug => (
            <div key={slug} className={`px-2.5 py-1 rounded-lg border text-xs ${isDark ? 'border-slate-600 bg-slate-800/30' : 'border-slate-200 bg-slate-50'}`}>
              <span className={`font-semibold ${text}`}>{labelFor(slug)}</span>
              <span className={muted}> {teamCounts[slug]?.home || 0}H / {teamCounts[slug]?.away || 0}A = {teamCounts[slug]?.total || 0}</span>
            </div>
          ))}
        </div>
      )}

      {/* View Mode + Filter Controls */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          {/* Season Selector */}
          <select value={selectedSeason} onChange={e => setSelectedSeason(e.target.value)}
            className={`h-7 text-xs rounded-md px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
            data-testid="season-selector"
            title="Season — current, next, and any past season that has games">
            {seasons.map(s => (
              <option key={s} value={s}>
                {s}{currentSeason && s < currentSeason ? ' (past)' : ''}{s === currentSeason ? ' (current)' : ''}
              </option>
            ))}
          </select>
          {/* Sport Selector — hidden when the parent pins the sport (the Scheduler
              page has one tab per sport, so an in-grid picker would be a second,
              conflicting control). "+ Add sport" moves to the hub's common row. */}
          {!lockedSport && (
          <select value={selectedSport} onChange={e => {
              if (e.target.value === '__add__') { setNewSportOpen(true); return; }
              setSelectedSport(e.target.value);
            }}
            className={`h-7 text-xs rounded-md px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
            data-testid="sport-selector"
            title="Sport — each sport gets its own grid and Google Calendar">
            {sports.length === 0 && <option value="bk">Basketball</option>}
            {sports.map(s => (
              <option key={s.slug} value={s.slug}>
                {s.label}{typeof s.slot_count === 'number' ? ` · ${s.slot_count}` : ''}
              </option>
            ))}
            <option value="__add__">+ Add sport…</option>
          </select>
          )}
          {/* Team level filter */}
          <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}
            className={`h-7 text-xs rounded-md px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
            data-testid="team-filter"
            title="Team level">
            <option value="all">All Teams</option>
            {teamCols.map(s => (
              <option key={s} value={s}>{labelFor(s)}</option>
            ))}
          </select>
          {/* Filter pills */}
          {[
            { key: 'all', label: 'All', icon: null },
            { key: 'games', label: 'Regular Season Games', icon: null },
            ...(isPractice ? [] : [{ key: 'events', label: 'Post Season & Events', icon: null }]),
            { key: 'practice', label: 'Practice', icon: null },
            { key: 'home', label: 'Home', icon: Home },
            { key: 'away', label: 'Away', icon: Plane },
          ].map(f => (
            <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'ghost'}
              className={`h-7 text-xs ${filter === f.key ? 'bg-amber-600 text-white' : muted}`}
              onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}>
              {f.icon && <f.icon className="h-3 w-3 mr-1" />}{f.label}
            </Button>
          ))}
        </div>
        <div className={`flex gap-0.5 p-0.5 rounded-md ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
          <Button size="sm" variant={viewMode === 'calendar' ? 'default' : 'ghost'}
            className={`h-7 text-xs px-2 ${viewMode === 'calendar' ? 'bg-amber-600 text-white' : muted}`}
            onClick={() => setViewMode('calendar')} data-testid="view-calendar">
            <Calendar className="h-3.5 w-3.5 mr-1" />Calendar
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'}
            className={`h-7 text-xs px-2 ${viewMode === 'list' ? 'bg-amber-600 text-white' : muted}`}
            onClick={() => setViewMode('list')} data-testid="view-list">
            <List className="h-3.5 w-3.5 mr-1" />List
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarGridView
          games={calendarGames}
          events={(filter === 'games' || filter === 'events') ? [] : practiceEvents}
          teamLabels={teamLabels}
          readOnly={readOnly}
          onDateClick={handleCalendarDateClick}
          onEventClick={handleCalendarEventClick}
          onEdit={(source) => openEdit(source)}
          onDelete={(source) => deleteSlot(source.id)}
          onReschedule={handleCalendarReschedule}
          filter={filter}
          teamFilter={teamFilter}
          teamColumns={teamColumns}
          tournaments={tournaments}
          orgId={orgId}
          sport={selectedSport}
          season={selectedSeason}
          onChanged={fetchSchedule}
          jump={calJump}
          opponents={opponents}
          onAddOpponent={(then) => setAddTeam({ then })}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (() => {
        // When "Practice" is selected, skip the games sections entirely
        // and render only the Practices & Events table below.
        const showGames = filter !== 'practice';
        const columns = [
          { key: 'date', label: 'Date', align: 'left', defaultW: 90 },
          { key: 'day', label: 'Day', align: 'left', defaultW: 44 },
          // Opponent column is games-only — practices have no opponent.
          ...(isPractice ? [] : [{ key: 'opponent', label: 'Opponent', align: 'left', defaultW: 200 }]),
          ...teamCols.map(s => ({ key: `team_${s}`, label: labelFor(s), align: 'center', defaultW: 54 })),
          { key: 'location', label: isPractice ? 'Practice Venue' : 'Location', align: 'left', defaultW: 170 },
          // Notes (AJ 2026-10-20): clipped in the cell, hover to read, click to add / edit / delete.
          { key: 'notes', label: 'Notes', align: 'left', defaultW: 160,
            tooltip: 'Notes for this row — OL only, never on the website or Google Calendar — plus an optional short Google Calendar note (calendar icon) that goes on the calendar event. Hover to read; click to add, edit or delete.' },
          // Post Season & Events only: is this row a game or a non-game event? (SCHEDULER_ROLE_PAGE)
          ...(!isPractice && filter === 'events' ? [{ key: 'event', label: 'Event', align: 'center', defaultW: 56,
            tooltip: 'Ticked = a non-game event (tryouts, team pictures, banquet, rally, meeting…). Events show on the website and Sabres app without "vs" / "@" and are not counted in the game totals. Unticked = a real game (e.g. a tournament or playoff match) — shown as "vs" / "@" and counted. OL pre-ticks rows whose name looks like an event; your choice always wins.' }] : []),
          { key: 'status', label: 'Status', align: 'center', defaultW: 88,
            tooltip: isPractice
              ? 'Practice status — Pending / Confirmed / Cancelled. Affects which practices appear on the published Google Calendar.'
              : 'Pending = tentative / awaiting confirmation. Confirmed = both teams locked in. A Pending game pushed to WP is published with time_tba so fans see "TBD".' },
          // Refs column is games-only — practices don't have referees.
          ...(isPractice ? [] : [{ key: 'refs_conf', label: 'Refs', align: 'center', defaultW: 48,
            tooltip: 'Green when the referee agency has been notified of this game. Any edit to the row re-sets this; publishing sends a diff-only update to the ref agency.' }]),
          { key: 'pub', label: 'Pub', align: 'center', defaultW: 60,
            tooltip: isPractice
              ? 'Google Calendar publish state. Practices never publish to WordPress (internal-only events).'
              : 'W and G are independent per-game controls — click W to publish/unpublish this game to WordPress only, click G for Google Calendar only. Green = published/synced, gray = not. The toolbar Publish button is the master that enqueues BOTH destinations. Unpublishing an already-final game asks for confirmation (it pulls a played game off the live site/calendar). Practices only touch Google Calendar.' },
          { key: 'actions', label: '', align: 'right', defaultW: 76 },
        ];
        const totalWidth = columns.reduce((sum, c) => sum + (colWidths[c.key] || c.defaultW), 0);
        // Left-frozen identity columns (Date, Day, Opponent) — stay visible when
        // scrolling the grid horizontally. Cumulative left offsets from column widths.
        const _cw = k => (colWidths[k] || (columns.find(c => c.key === k) || {}).defaultW || 0);
        const stickyCols = isPractice ? ['date', 'day'] : ['date', 'day', 'opponent'];
        const stickyLeftOf = { date: 0, day: _cw('date'), opponent: _cw('date') + _cw('day') };

        return (
        <>
        {showGames && (<>
        <Card className={bg}>
          <CardContent className="pt-3 pb-2">
            <div className="overflow-auto pb-1 max-h-[70vh] rounded-md border border-transparent" style={{ scrollbarWidth: 'auto' }} data-testid="schedule-scroll">
            <table className="text-sm border-collapse"
              /* The table was pinned to the SUM of its column widths, so on a wide
                 screen it stopped short and left the rest of the card empty — 1154px
                 of a 1590px container (AJ, 2026-10-18). `width: 100%` makes it fill
                 the card and `minWidth` keeps the old behaviour on a narrow screen,
                 where the container still scrolls horizontally. */
              style={{ tableLayout: 'fixed', width: '100%', minWidth: totalWidth }}
              data-testid="schedule-table" ref={tableRef}><colgroup>
                {columns.map(col => (
                  <col key={col.key} style={{ width: colWidths[col.key] || col.defaultW }} />
                ))}
              </colgroup>
              <thead>
                <tr className={`border-b ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                  {columns.map((col) => (
                    <th key={col.key}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 15,
                        background: isDark ? '#1e293b' : '#ffffff',
                        textAlign: col.align,
                        overflow: 'visible',
                        boxShadow: isDark ? 'inset 0 -1px 0 #475569' : 'inset 0 -1px 0 #e2e8f0',
                        ...(stickyCols.includes(col.key) ? { left: stickyLeftOf[col.key], zIndex: 25 } : {}),
                        ...(col.key === 'actions' ? { right: 0, zIndex: 25 } : {}),
                      }}
                      className={`py-2 px-2 text-xs font-medium ${muted} select-none`}
                      title={col.tooltip || undefined}>
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.tooltip && (
                          col.key === 'pub' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); openPublishPreview('publish'); }}
                              className="hover:text-amber-500 transition"
                              title={`${col.tooltip}\n\nClick to open Publish All preview.`}
                              data-testid="pub-header-info"
                            >
                              <Info className="h-3 w-3" />
                            </button>
                          ) : col.key === 'refs_conf' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSettingsTab('referee'); setSettingsOpen(true); }}
                              className="hover:text-amber-500 transition"
                              title={`${col.tooltip}\n\nClick to edit referee agency settings.`}
                              data-testid="refs-header-info"
                            >
                              <Info className="h-3 w-3" />
                            </button>
                          ) : (
                            <Info className="h-3 w-3 opacity-60" />
                          )
                        )}
                      </span>
                      {col.key !== 'actions' && (
                        <div
                          onMouseDown={(e) => startResize(e, col.key)}
                          style={{
                            position: 'absolute', top: 0, right: -6, bottom: 0, width: 12,
                            cursor: 'col-resize', zIndex: 20,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          data-testid={`resize-${col.key}`}
                        >
                          <div style={{
                            width: 2, height: '60%', borderRadius: 1,
                            background: isDark ? 'rgba(148,163,184,0.4)' : 'rgba(203,213,225,0.8)',
                          }} />
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {listGames.length === 0 && (
                  <tr><td colSpan={columns.length} className={`py-8 text-center ${muted}`}>No {isPractice ? 'practices' : 'games'} scheduled. Click "Add {isPractice ? 'Practice' : 'Game'}" to get started.</td></tr>
                )}
                {listGames.map((slot, slotIdx) => {
                  const fmtDay = (ds) => { const [y, m, d] = (ds || '').split('-').map(Number); return y ? new Date(y, m - 1, d).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' }) : ''; };
                  // A tournament's rows collapse into one summary row (TEAM_TOURNAMENTS.md §4).
                  const tid = (slot.games || []).find(g => g.tournament_id)?.tournament_id;
                  const tourn = tid && tournaments.find(t => t.id === tid);
                  if (tourn && !openTourn[tid]) {
                    const firstIdx = listGames.findIndex(sl => (sl.games || []).some(g => g.tournament_id === tid));
                    if (firstIdx !== slotIdx) return null;
                    const tSlots = listGames.filter(sl => (sl.games || []).some(g => g.tournament_id === tid));
                    const nGames = tSlots.reduce((n, sl) => n + (sl.games || []).filter(g => g.tournament_id === tid).length, 0);
                    const nHeld = tSlots.reduce((n, sl) => n + (sl.games || []).filter(g => g.tournament_id === tid && g.slot_state).length, 0);
                    return (
                      <tr key={`tourn-${tid}`} className={`border-b ${isDark ? 'border-slate-700/50 bg-amber-900/20' : 'border-slate-200/60 bg-amber-50'}`} data-testid={`tourn-row-${tid}`}>
                        <td colSpan={columns.length} className={`py-2 px-2 text-sm ${text}`}>
                          <span className="font-semibold">🏆 {tourn.name}</span>
                          <span className={`ml-2 text-xs ${muted}`}>{fmtDay(tourn.start_date)}{tourn.end_date && tourn.end_date !== tourn.start_date ? ` – ${fmtDay(tourn.end_date)}` : ''}{tourn.location ? ` · ${tourn.location}` : ''} · {nGames} games{nHeld ? ` · ${nHeld} held` : ''}</span>
                          <button type="button" className="ml-3 text-xs text-amber-700 hover:underline" onClick={() => setOpenTourn(o => ({ ...o, [tid]: true }))} data-testid={`tourn-expand-${tid}`}>Show games</button>
                          <button type="button" className="ml-3 text-xs text-amber-700 hover:underline" data-testid={`tourn-cal-${tid}`}
                            onClick={() => { setCalJump({ date: tourn.start_date, n: Date.now() }); setViewMode('calendar'); }}>Open in Calendar</button>
                          {!readOnly && (
                            <button type="button" className="float-right text-red-400 hover:text-red-300" title="Delete this tournament's games"
                              data-testid={`tourn-del-${tid}`} onClick={() => deleteTournamentGames(tourn)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  }
                  const tournHeader = tourn && openTourn[tid]
                    && listGames.findIndex(sl => (sl.games || []).some(g => g.tournament_id === tid)) === slotIdx;
                  const gamesMap = {};
                  (slot.games || []).forEach(g => { gamesMap[g.team_slug] = g.time; });
                  const isExpanded = expandedSlot === slot.id;
                  const rowBg = slot.home_away === 'home'
                    ? (isDark ? 'rgba(16,185,129,0.12)' : 'transparent')
                    : (isDark ? 'rgba(239,68,68,0.22)' : '#fef2f2');
                  const cellStyle = { backgroundColor: rowBg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  // Opaque bg for the frozen identity cells so scrolled content
                  // never bleeds through them.
                  const rowBgSolid = slot.home_away === 'home'
                    ? (isDark ? '#1e293b' : '#ffffff')
                    : (isDark ? '#3a2020' : '#fef2f2');
                  const stickyBody = key => (stickyCols.includes(key)
                    ? { position: 'sticky', left: stickyLeftOf[key], zIndex: 5, backgroundColor: rowBgSolid }
                    : {});

                  return (
                    <React.Fragment key={slot.id}>
                      {tournHeader && (
                        <tr className={isDark ? 'bg-amber-900/20' : 'bg-amber-50'}>
                          <td colSpan={columns.length} className={`py-1 px-2 text-xs ${text}`}>
                            🏆 {tourn.name}
                            <button type="button" className="ml-3 text-amber-700 hover:underline" onClick={() => setOpenTourn(o => ({ ...o, [tid]: false }))}>Collapse</button>
                          </td>
                        </tr>
                      )}
                      <tr className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200/60'} transition-colors`}
                        data-testid={`slot-row-${slot.id}`}>
                        <td className={`py-2 px-2 ${text}`} style={{ ...cellStyle, ...stickyBody('date') }}>
                          <InlineDate value={slot.date} isDark={isDark}
                            onSave={v => patchSlotField(slot.id, 'date', v)}
                            readOnly={readOnly}
                            testId={`cell-date-${slot.id}`} />
                        </td>
                        <td className={`py-2 px-2 text-xs ${muted}`} style={{ ...cellStyle, ...stickyBody('day') }}>{slot.day_of_week}</td>
                        {!isPractice && (
                          <td className={`py-2 px-2 font-medium ${text}`} style={{ ...cellStyle, ...stickyBody('opponent') }} title={slot.opponent}>
                            <InlineListSelect value={slot.opponent} isDark={isDark}
                              options={opponents}
                              onSave={v => patchSlotField(slot.id, 'opponent', v)}
                              onAddNew={() => setAddTeam({ then: (name) => patchSlotField(slot.id, 'opponent', name) })}
                              readOnly={readOnly}
                              testId={`cell-opponent-${slot.id}`}
                              emptyLabel={slot.opponent ? undefined : (/(tournament|classic|invitational|showcase|jamboree|sports world|tourney)/i.test(slot.location || '') ? 'TBD · Tournament' : 'TBD')}
                              placeholder="Opponent…" />
                          </td>
                        )}
                        {teamCols.map(s => (
                          <td key={s} className="py-2 px-1 text-center" style={{ backgroundColor: rowBg }}>
                            {(() => {
                              // A team cell holding a game opens the SAME per-game editor as
                              // Calendar → By team (court, round, state, opponent, On the website,
                              // Delete) — TEAM_TOURNAMENTS.md §9. An empty cell adds a time inline.
                              const mine = (slot.games || []).filter(g => g.team_slug === s && g.game_id);
                              if (readOnly || !mine.length) {
                                return <InlineTeamTime time={gamesMap[s] != null ? gamesMap[s] : null}
                                  isDark={isDark}
                                  onSave={v => patchTeamTime(slot.id, s, v)}
                                  readOnly={readOnly}
                                  testId={`cell-time-${slot.id}-${s}`} />;
                              }
                              return (
                                <span className="inline-flex flex-col items-center gap-0.5">
                                  {mine.map(g => (
                                    <button key={g.game_id} type="button" data-testid={`cell-game-${g.game_id}`}
                                      title={[g.venue_name, g.round, g.slot_state === 'held' ? 'Slot held' : g.slot_state === 'if_win' ? 'If we win' : ''].filter(Boolean).join(' · ') || 'Edit this game'}
                                      onClick={() => setListEdit({
                                        gameId: g.game_id, teamSlug: s, teamLabel: labelFor(s), date: slot.date,
                                        startMin: timeToMin(g.time), opponent: slot.opponent, opponentSlug: slot.opponent_slug, venueSlug: g.venue_slug, venue: g.venue_name,
                                        round: g.round, slotState: g.slot_state, tournamentId: g.tournament_id,
                                        homeAway: slot.home_away, published: g.published,
                                      })}
                                      className={`text-xs px-1.5 py-0.5 rounded ${g.slot_state ? 'border border-dashed' : ''} ${
                                        isTbdTime(g.time)
                                          ? (isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700')
                                          : (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                                      }`}>{displayTime(g.time)}</button>
                                  ))}
                                </span>
                              );
                            })()}
                          </td>
                        ))}
                        <td className={`py-2 px-2 text-xs ${muted}`} style={cellStyle} title={slot.location}>
                          <InlineListSelect value={slot.location} isDark={isDark}
                            options={venues}
                            onSave={v => patchSlotField(slot.id, 'location', v)}
                            onAddNew={() => setAddVenue({ then: (name) => patchSlotField(slot.id, 'location', name) })}
                            addLabel="+ Add venue…"
                            readOnly={readOnly}
                            testId={`cell-location-${slot.id}`}
                            placeholder="Location…" />
                        </td>
                        <td className="py-2 px-2" style={{ ...cellStyle, maxWidth: _cw('notes') }}>
                          <ScheduleNotesCell value={slot.notes} calendarNote={slot.calendar_notes} isDark={isDark} readOnly={readOnly}
                            title={[slot.date, slot.opponent].filter(Boolean).join(' · ')}
                            onSave={fields => patchSlotFields(slot.id, fields)}
                            testId={`cell-notes-${slot.id}`} />
                        </td>
                        {!isPractice && filter === 'events' && (
                          <td className="py-2 px-1 text-center" style={{ backgroundColor: rowBg }}>
                            <input type="checkbox" disabled={readOnly}
                              checked={slot.event_flag != null ? !!slot.event_flag : slot.is_competition === false}
                              onChange={e => setEventFlag(slot, e.target.checked)}
                              title="Ticked = non-game event (no vs/@, not counted as a game)"
                              data-testid={`cell-event-${slot.id}`} />
                          </td>
                        )}
                        <td className="py-2 px-1 text-center" style={{ backgroundColor: rowBg }}>
                          <InlineSelect value={slot.scheduling_status || 'pending'} isDark={isDark}
                            onSave={v => patchSlotField(slot.id, 'scheduling_status', v)}
                            readOnly={readOnly}
                            testId={`cell-status-${slot.id}`}
                            options={[
                              { value: 'pending', label: 'pending' },
                              { value: 'confirmed', label: 'confirmed' },
                              { value: 'cancelled', label: 'cancelled' },
                            ]}
                            render={v => (
                              <Badge className={`${statusColor(v)} text-[10px] px-1.5`}>{displayStatus(v)}</Badge>
                            )} />
                        </td>
                        {!isPractice && (
                          <td className="py-2 px-1 text-center" style={{ backgroundColor: rowBg }}>
                            {!readOnly && (
                            <button
                              onClick={e => { e.stopPropagation(); notifyRefsOne(slot); }}
                              className={`mx-auto flex items-center justify-center h-6 w-6 rounded-full transition ${slot.refs_notified ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-slate-300 text-slate-600 hover:bg-slate-400'}`}
                              data-testid={`refs-${slot.id}`}
                              title={slot.refs_notified
                                ? `Referee agency notified${slot.refs_notified_at ? ` on ${new Date(slot.refs_notified_at).toLocaleString()}` : ''} — click to resend`
                                : 'Referee agency not yet notified — click to send'}
                            >
                              <Mail className="h-3 w-3" />
                            </button>
                            )}
                          </td>
                        )}
                        <td className="py-2 px-1 text-center" style={{ backgroundColor: rowBg }}>
                          {(() => {
                            // Read-only (AD view): show publish STATE as non-clickable badges.
                            if (readOnly) {
                              const wp = pubOn(slot, 'wp'), gc = pubOn(slot, 'gcal');
                              const dot = (on) => `inline-flex items-center justify-center h-3.5 w-3.5 rounded-full text-[9px] font-bold ${on ? 'bg-emerald-500 text-white' : (isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-300 text-slate-600')}`;
                              return (
                                <div className="mx-auto flex items-center justify-center gap-0.5" data-testid={`pub-${slot.id}`} title="Publish state (read-only): W = WordPress, G = Google Calendar">
                                  {!isPractice && <span className={dot(wp)} data-testid={`pub-wp-${slot.id}`}>W</span>}
                                  <span className={dot(gc)} data-testid={`pub-gc-${slot.id}`}>G</span>
                                </div>
                              );
                            }
                            // Practice mode: GCal only (no W indicator).
                            if (isPractice) {
                              const gc = pubOn(slot, 'gcal');
                              const btnCls = gc
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                : 'bg-slate-500 text-white hover:bg-slate-600';
                              const tooltip = gc
                                ? `On Google Calendar${slot.gcal_pushed_at ? ' since ' + new Date(slot.gcal_pushed_at).toLocaleString() : ''}. Click to remove from calendar. Practices never publish to WordPress.`
                                : 'Not on Google Calendar. Click to publish. Practices never publish to WordPress.';
                              return (
                                <button
                                  onClick={e => { e.stopPropagation(); togglePublishSlot(slot); }}
                                  className={`mx-auto flex items-center justify-center h-6 w-7 rounded-full transition ${btnCls}`}
                                  data-testid={`pub-${slot.id}`}
                                  title={tooltip}
                                >
                                  <span className={`inline-flex items-center justify-center h-3.5 w-3.5 rounded-full text-[9px] font-bold ${gc ? 'bg-white/90 text-emerald-700' : 'bg-white/20 text-white/60'}`}
                                        data-testid={`pub-gc-${slot.id}`}>G</span>
                                </button>
                              );
                            }
                            // Game mode: dual W+G indicator.
                            const wp = pubOn(slot, 'wp');
                            const gc = pubOn(slot, 'gcal');
                            const both = wp && gc;
                            const partial = (wp || gc) && !both;
                            const btnCls = both
                              ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                              : partial
                                ? 'bg-amber-500 text-white hover:bg-amber-600'
                                : 'bg-slate-500 text-white hover:bg-slate-600';
                            const tooltip = both
                              ? `Published — WP ${slot.wp_pushed_at ? 'on ' + new Date(slot.wp_pushed_at).toLocaleString() : ''} + Google Calendar. Click to unpublish both.`
                              : (() => {
                                  const wpDrift = slot.wp_drift_detected_at
                                    ? `WP drift detected ${new Date(slot.wp_drift_detected_at).toLocaleString()} (someone edited in WP admin or OL edit since last publish).`
                                    : null;
                                  const gcDrift = slot.gcal_drift_detected_at
                                    ? `GCal drift detected ${new Date(slot.gcal_drift_detected_at).toLocaleString()} (event removed from Google Calendar).`
                                    : null;
                                  if (wp && !gc) return `Pushed to WordPress; Google Calendar sync pending${gcDrift ? ' — ' + gcDrift : ''}. Click to publish.`;
                                  if (gc && !wp) return `On Google Calendar; WordPress push pending${wpDrift ? ' — ' + wpDrift : ''}. Click to publish.`;
                                  const drifts = [wpDrift, gcDrift].filter(Boolean).join(' ');
                                  return `Not in sync${drifts ? ' — ' + drifts : ''}. Click to publish to both.`;
                                })();
                            return (
                              <div
                                className={`mx-auto flex items-center justify-center h-6 w-10 rounded-full transition ${btnCls}`}
                                data-testid={`pub-${slot.id}`}
                                title={tooltip}
                              >
                                <div className="flex items-center gap-0.5 text-[9px] font-bold tracking-wide leading-none">
                                  <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); togglePublishDest(slot, 'wp'); }}
                                    className={`inline-flex items-center justify-center h-3.5 w-3.5 rounded-full transition ${wp ? 'bg-white/90 text-emerald-700' : 'bg-white/20 text-white/60'}`}
                                    data-testid={`pub-wp-${slot.id}`}
                                    title={wp
                                      ? 'On WordPress — click to remove from the website only (leaves Google Calendar untouched).'
                                      : 'Not on WordPress — click to publish to the website only (does not touch Google Calendar).'}
                                  >W</button>
                                  <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); togglePublishDest(slot, 'gcal'); }}
                                    className={`inline-flex items-center justify-center h-3.5 w-3.5 rounded-full transition ${gc ? 'bg-white/90 text-emerald-700' : 'bg-white/20 text-white/60'}`}
                                    data-testid={`pub-gc-${slot.id}`}
                                    title={gc
                                      ? 'On Google Calendar — click to remove from the calendar only (leaves WordPress untouched).'
                                      : 'Not on Google Calendar — click to publish to the calendar only (does not touch WordPress).'}
                                  >G</button>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-1 text-right"
                          style={{ position: 'sticky', right: 0, zIndex: 5, backgroundColor: rowBgSolid }}>
                          {!readOnly && (
                          <div className="flex justify-end gap-1.5 pr-1">
                            <button onClick={e => { e.stopPropagation(); openEdit(slot); }} className="text-blue-400 hover:text-blue-300 shrink-0" data-testid={`edit-${slot.id}`} title="Open full editor (image/all fields)">
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={e => { e.stopPropagation(); deleteSlot(slot.id); }} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`del-${slot.id}`} title="Delete">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={isDark ? 'bg-slate-700/20' : 'bg-blue-50/30'}>
                          <td colSpan={columns.length} className="py-3 px-4">
                            <div className="flex flex-wrap gap-4 text-xs">
                              {slot.location_address && (
                                <div><MapPin className={`h-3 w-3 inline mr-1 ${muted}`} /><span className={muted}>Address: </span><span className={text}>{slot.location_address}</span></div>
                              )}
                              {slot.notes && (
                                <div><span className={muted}>Notes: </span><span className={text}>{slot.notes}</span></div>
                              )}
                              <div className="flex gap-2">
                                <span className={muted}>Status:</span>
                                {readOnly ? (
                                  <span className={`px-2 py-0.5 rounded text-[10px] ${statusColor(displayStatus(slot.scheduling_status))} text-white`}>
                                    {displayStatus(slot.scheduling_status)}
                                  </span>
                                ) : ['pending', 'confirmed'].map(s => (
                                  <button key={s} onClick={() => updateStatus(slot, s)}
                                    className={`px-2 py-0.5 rounded text-[10px] ${displayStatus(slot.scheduling_status) === s ? statusColor(s) + ' text-white' : isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                    {s}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className={`mt-2 px-2 py-1 text-[11px] flex items-center gap-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="inline-edit-hint">
              <Edit2 className="h-3 w-3 text-amber-400" />
              <span>Click any cell to edit. Tab / Enter saves, Esc cancels. Per-team time cells open a time picker on click.</span>
            </div>
          </CardContent>
        </Card>
        </>)}

        {/* Practice & Events section — pulled from the same data source the
            Calendar view uses (team_events collection via /practice-events),
            so List and Calendar stay in sync. Games and Practices are
            intentionally separate: games get pushed to WP (time_tba-aware),
            practices stay OL-local (no WP counterpart). */}
        {(filter === 'all' || filter === 'practice') && practiceEvents.length > 0 && (() => {
          const practiceRows = teamFilter === 'all'
            ? practiceEvents
            : practiceEvents.filter(p => !p.team_slug || p.team_slug === teamFilter);
          if (practiceRows.length === 0) return null;
          return (
            <Card className={`${isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'} mt-4`}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm ${text} flex items-center gap-2`}>
                  <Calendar className="h-4 w-4 text-blue-500" />
                  Practices &amp; Events
                  <Badge variant="outline" className="text-[10px]">{practiceRows.length}</Badge>
                  <span className={`text-[11px] font-normal ${muted}`}>
                    · OL-only (not pushed to WordPress)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Date</th>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Day</th>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Type</th>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Time</th>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Team</th>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Location</th>
                      <th className={`py-2 px-3 text-left ${muted} font-medium`}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {practiceRows.map((p, i) => (
                      <tr key={p.id || i} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200/60'}`}>
                        <td className={`py-2 px-3 font-mono ${text}`}>{p.date}</td>
                        <td className={`py-2 px-3 ${muted}`}>{getDayOfWeek(p.date)}</td>
                        <td className={`py-2 px-3`}>
                          <Badge className="bg-blue-600 text-[10px]">{p.event_type || 'practice'}</Badge>
                        </td>
                        <td className={`py-2 px-3 ${text}`}>
                          <span className={`px-1.5 py-0.5 rounded ${isTbdTime(p.time) ? (isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700') : ''}`}>
                            {displayTime(p.time)}
                          </span>
                        </td>
                        <td className={`py-2 px-3 ${text}`}>{p.team_slug ? (teamLabels[p.team_slug] || p.team_slug.toUpperCase()) : 'All Teams'}</td>
                        <td className={`py-2 px-3 ${muted}`}>{p.location || '-'}</td>
                        <td className={`py-2 px-3 ${muted}`}>{p.notes || p.title || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })()}
        </>
      );
      })()}

      {/* Add/Edit Dialog - Enhanced with dropdowns */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={text}>
              {editSlot
                ? (isPractice ? 'Edit Practice' : 'Edit Game Slot')
                : (isPractice ? 'Add Practice' : 'Add Game Slot')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="game-slot-dialog">
            {!isPractice && (
              <label className={`flex items-center gap-2 text-xs ${muted}`} data-testid="slot-kind">
                <input type="checkbox" checked={form.slot_kind === 'event'}
                  onChange={e => setForm(p => ({ ...p, slot_kind: e.target.checked ? 'event' : 'game' }))} />
                Post Season &amp; Events (tournaments, playoffs, banquet, tryouts, rallies…) — still publishes and counts like a game
              </label>
            )}
            <div className={`grid ${isPractice ? 'grid-cols-2' : 'grid-cols-3'} gap-3`}>
              <div>
                <Label className={`text-xs ${muted}`}>{(!editSlot && !isPractice) ? 'Date (first day)' : 'Date'}</Label>
                <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                  className={inputCls} data-testid="slot-date" />
                {!editSlot && !isPractice && (
                  <div className="mt-1">
                    <Label className={`text-xs ${muted}`} title="A tournament or other multi-day event: the same opponent, teams, times and location are added on every day through this date. The same opponent on consecutive days becomes a tournament (🏆) automatically; edit each day's games in Calendar ▸ Week ▸ By team.">
                      Last day (multi-day event, optional)
                    </Label>
                    <Input type="date" value={form.last_date || ''} min={form.date || undefined}
                      onChange={e => setForm(p => ({ ...p, last_date: e.target.value }))}
                      className={inputCls} data-testid="slot-last-date" />
                  </div>
                )}
              </div>
              {!isPractice && (
                <div>
                  <Label className={`text-xs ${muted}`}>Home / Away</Label>
                  <select value={form.home_away} onChange={e => {
                      const ha = e.target.value;
                      setForm(p => {
                        const auto = venueFor(ha, p.opponent);
                        // A PREFILLED location follows Home/Away (Home → our field, Away →
                        // the opponent's, else empty); one the user picked is never changed.
                        // Before: switching to Away kept our home field (AJ 2026-10-20).
                        return { ...p, home_away: ha, location: (p._loc_auto || !p.location) ? auto : p.location,
                                 _loc_auto: p._loc_auto || !p.location };
                      });
                    }}
                    className={`w-full h-10 rounded-md px-3 border ${inputCls}`} data-testid="slot-ha">
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </div>
              )}
              <div>
                <Label className={`text-xs ${muted} flex items-center gap-1`}>
                  Status
                  <span title={isPractice
                    ? 'Practice status. Cancelled practices stay on the calendar but are visually crossed-out so the team knows.'
                    : "Where the game stands with the opponent. Pending = we requested or agreed tentatively — time/venue may still change. Confirmed = both teams have locked in. Games pushed to WP while still Pending are published with time_tba so fans see 'TBD'."}>
                    <Info className="h-3 w-3 opacity-60" />
                  </span>
                </Label>
                <select value={form.scheduling_status} onChange={e => setForm(p => ({ ...p, scheduling_status: e.target.value }))}
                  className={`w-full h-10 rounded-md px-3 border ${inputCls}`} data-testid="slot-status">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  {isPractice && <option value="cancelled">Cancelled</option>}
                </select>
              </div>
            </div>

            <div className={`grid ${isPractice ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
              {!isPractice && (
                <div>
                  <Label className={`text-xs ${muted}`}>Opponent</Label>
                  <select value={form.opponent} onChange={e => {
                      const opp = e.target.value;
                      if (opp === ADD_NEW) {
                        setAddTeam({ then: (name) => setForm(p => ({ ...p, opponent: name,
                          location: (p.home_away === 'away' && (p._loc_auto || !p.location)) ? venueFor(p.home_away, name) : p.location })) });
                        return;
                      }
                      setForm(p => {
                        // AWAY: auto-fill the opponent's home venue when the
                        // location is still empty (editable afterward).
                        const auto = venueFor(p.home_away, opp);
                        return { ...p, opponent: opp, location: (p.home_away === 'away' && (p._loc_auto || !p.location)) ? auto : p.location };
                      });
                    }}
                    className={`${inputCls} h-9`} data-testid="slot-opponent">
                    <option value="">— select opponent —</option>
                    <option value={ADD_NEW}>+ Add New…</option>
                    {form.opponent && !opponents.includes(form.opponent) && (
                      <option value={form.opponent}>{form.opponent} (current)</option>
                    )}
                    {opponents.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className={`text-xs ${muted} flex items-center gap-1`}>
                  {isPractice ? 'Practice Venue' : 'Location'}
                  {!isPractice && (
                    <span title="Auto-filled from the home venue — HOME uses your squad's home venue, AWAY uses the opponent's home venue (when known). You can change it.">
                      <Info className="h-3 w-3 opacity-60" />
                    </span>
                  )}
                </Label>
                <select value={form.location} onChange={e => {
                    const loc = e.target.value;
                    if (loc === ADD_NEW) { setAddVenue({ then: (name) => setForm(p => ({ ...p, location: name, _loc_auto: false })) }); return; }
                    setForm(p => ({ ...p, location: loc, _loc_auto: false }));
                  }}
                  className={`${inputCls} h-9`} data-testid="slot-location">
                  <option value="">{isPractice ? '— select practice venue —' : '— select venue —'}</option>
                  <option value={ADD_NEW}>+ Add venue…</option>
                  {form.location && !venues.includes(form.location) && (
                    <option value={form.location}>{form.location} (current)</option>
                  )}
                  {venues.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            {!isPractice && form.home_away === 'away' && (
              <div>
                <Label className={`text-xs ${muted}`}>Location Address</Label>
                <Input value={form.location_address} onChange={e => setForm(p => ({ ...p, location_address: e.target.value }))}
                  placeholder="Full address for away games" className={inputCls} data-testid="slot-address" />
              </div>
            )}

            {/* Team times grid */}
            <div>
              <Label className={`text-xs ${muted} mb-2 block`}>Team Game Times (leave blank if team not playing)</Label>
              <div className="grid grid-cols-4 gap-2">
                {teamCols.map(slug => (
                  <div key={slug} className={`p-2 rounded-lg border ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                    <Label className={`text-xs font-semibold ${text} mb-1 block`}>{labelFor(slug)}</Label>
                    <Input type="time" value={to24h(form.games[slug])}
                      onChange={e => {
                        const val = e.target.value;
                        if (val) {
                          const formatted = to12h(val);
                          setForm(p => ({ ...p, games: { ...p.games, [slug]: formatted } }));
                        } else {
                          setForm(p => {
                            const g = { ...p.games };
                            delete g[slug];
                            return { ...p, games: g };
                          });
                        }
                      }}
                      className={`h-8 text-xs ${inputCls}`} data-testid={`time-${slug}`} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className={`text-xs ${muted}`}>Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional scheduling notes" className={inputCls} data-testid="slot-notes" />
            </div>

            <div className="flex items-center gap-4">
              <label className={`flex items-center gap-2 text-sm ${text} cursor-pointer`}>
                <input type="checkbox" checked={form.team_confirmed} onChange={e => setForm(p => ({ ...p, team_confirmed: e.target.checked }))} />
                Team Confirmed
              </label>
              {!isPractice && (
                <label className={`flex items-center gap-2 text-sm ${text} cursor-pointer`}>
                  <input type="checkbox" checked={form.refs_confirmed} onChange={e => setForm(p => ({ ...p, refs_confirmed: e.target.checked }))} />
                  Refs Confirmed
                </label>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAddDialog(false)} className={muted}>Cancel</Button>
              <Button onClick={saveSlot}
                disabled={saving || !form.date || (!isPractice && !form.opponent) || (isPractice && Object.keys(form.games || {}).length === 0)}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-slot-btn"
                title={isPractice && Object.keys(form.games || {}).length === 0 ? 'Add at least one team time before saving' : ''}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />{editSlot ? 'Update' : 'Create'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referee Agency Configuration — retained for legacy openers; main
          settings entry is the unified Settings dialog below. */}
      {/* "+ Add New" popups — same Teams / Venues data as WP Mgmt (OPPONENTS_UI.md) */}
      {listEdit && (
        <GameEditDialog o={listEdit} orgId={orgId} sport={selectedSport} season={selectedSeason}
          tournaments={tournaments} opponents={opponents} onAddOpponent={(then) => setAddTeam({ then })}
          isDark={isDark} readOnly={readOnly} onClose={() => setListEdit(null)} onDone={fetchSchedule} />
      )}
      <AddTeamModal open={!!addTeam} orgId={orgId} onClose={() => setAddTeam(null)}
        onCreated={(row) => {
          const name = row?.title || row?.name || '';
          if (!name) return;
          setOpponents(o => (o.includes(name) ? o : [...o, name].sort((a, b) => a.localeCompare(b))));
          addTeam?.then?.(name);
        }} />
      <AddVenueModal open={!!addVenue} orgId={orgId} onClose={() => setAddVenue(null)}
        onCreated={(row) => {
          const name = row?.title || row?.name || '';
          if (!name) return;
          setVenues(v => (v.includes(name) ? v : [...v, name].sort((a, b) => a.localeCompare(b))));
          addVenue?.then?.(name);
        }} />
      <Dialog open={refConfigOpen} onOpenChange={setRefConfigOpen}>
        <DialogContent className={`max-w-lg ${bg}`}>
          <DialogHeader>
            <DialogTitle className={text}>Referee Agency Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className={`text-xs ${muted}`}>
              When you publish the schedule, OmniLeagues will notify this contact for every game that
              has not yet been sent. If an already-notified game is edited, the <Mail className="h-3 w-3 inline mx-0.5" />
              icon on that row resets and the next publish will send a <strong>change notice</strong>
              with only the games that changed.
            </p>
            <div>
              <Label className={`text-xs ${muted}`}>Agency Name</Label>
              <Input value={refConfig.agency_name || ''}
                onChange={e => setRefConfig(p => ({ ...p, agency_name: e.target.value }))}
                placeholder="e.g. Triangle Area Basketball Officials"
                className={inputCls} data-testid="ref-agency-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={`text-xs ${muted}`}>Contact Name</Label>
                <Input value={refConfig.contact_name || ''}
                  onChange={e => setRefConfig(p => ({ ...p, contact_name: e.target.value }))}
                  className={inputCls} />
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Phone (SMS)</Label>
                <Input type="tel" value={refConfig.phone || ''}
                  onChange={e => setRefConfig(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 555 123 4567"
                  className={inputCls} data-testid="ref-phone" />
              </div>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Email</Label>
              <Input type="email" value={refConfig.email || ''}
                onChange={e => setRefConfig(p => ({ ...p, email: e.target.value }))}
                placeholder="assignor@refagency.com"
                className={inputCls} data-testid="ref-email" />
            </div>
            <div className="flex items-center gap-4">
              <label className={`flex items-center gap-2 text-sm ${text} cursor-pointer`}>
                <input type="checkbox" checked={!!refConfig.email_enabled}
                  onChange={e => setRefConfig(p => ({ ...p, email_enabled: e.target.checked }))} />
                Send email notifications
              </label>
              <label className={`flex items-center gap-2 text-sm ${text} cursor-pointer`}>
                <input type="checkbox" checked={!!refConfig.sms_enabled}
                  onChange={e => setRefConfig(p => ({ ...p, sms_enabled: e.target.checked }))} />
                Send SMS notifications
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setRefConfigOpen(false)} className={muted}>Cancel</Button>
              <Button onClick={saveRefConfig} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ref-config-save-btn">
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Sport Dialog */}
      <Dialog open={newSportOpen} onOpenChange={setNewSportOpen}>
        <DialogContent className={`max-w-sm ${bg}`}>
          <DialogHeader>
            <DialogTitle className={text}>Add a sport</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2" data-testid="new-sport-dialog">
            <p className={`text-xs ${muted}`}>
              The sport is stored locally first. On your next Publish, OmniLeagues
              will create it on WordPress automatically.
            </p>
            <div>
              <Label className={`text-xs ${muted}`}>Sport name</Label>
              <Input value={newSportLabel}
                onChange={e => setNewSportLabel(e.target.value)}
                placeholder="Basketball, Baseball or Volleyball"
                autoFocus
                className={inputCls} data-testid="new-sport-label" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setNewSportOpen(false); setNewSportLabel(''); }} className={muted}>Cancel</Button>
              <Button onClick={addSport} disabled={!newSportLabel.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="new-sport-save">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Settings Dialog — Referees + Google Calendars per sport */}
      {/* Schedule Settings moved to its own component (2026-10-08) — every setting
          in it is ORG-WIDE, so the Scheduler hub renders it from the common row
          above the sport tabs and can open it from any sub-tab. It loads its own
          data, so it only needs an orgId. See SCHEDULER_ROLE_PAGE.md §4. */}
      <ScheduleSettingsDialog
        orgId={orgId}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        initialTab={settingsTab}
        isDark={isDark}
        onSaved={(st) => { if (st) setRefSettings({ referee: st.referee || {}, referee_by_sport: st.referee_by_sport || {} }); }}
      />
      {/* Publish Preview Dialog — 2nd confirmation before hitting WP/GCal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`max-w-3xl ${bg}`}>
          <DialogHeader>
            <DialogTitle className={`${text} flex items-center gap-2`}>
              {previewMode === 'unpublish'
                ? <XCircle className="h-5 w-5 text-red-500" />
                : <Send className="h-5 w-5 text-amber-500" />}
              {previewMode === 'unpublish' ? 'Confirm Unpublish ALL' : previewMode === 'changed' ? 'Confirm Publish changed' : 'Confirm Publish ALL'}
            </DialogTitle>
          </DialogHeader>
          {previewLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
            </div>
          )}
          {!previewLoading && previewData && (
            <div className="space-y-3 text-sm" data-testid="publish-preview">
              <div className={`rounded-lg p-3 border ${isDark ? 'bg-slate-900/30 border-slate-700' : 'bg-amber-50 border-amber-200'}`}>
                <div className={`font-semibold ${text} mb-1`}>
                  Scope: <span className="text-amber-500">{selectedSport.toUpperCase()}</span> · {selectedSeason}
                </div>
                <div className={`text-xs ${muted}`}>
                  {previewMode === 'unpublish'
                    ? 'The following rows will be REMOVED from WordPress and Google Calendar.'
                    : 'The following rows will be PUBLISHED to WordPress and queued for Google Calendar.'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-lg border p-3 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className={`text-xs ${muted}`}>Games affected</div>
                  <div className={`text-2xl font-semibold ${text}`} data-testid="preview-games-count">{previewData.counts.games}</div>
                  <div className={`text-[10px] ${muted}`}>WP Bridge · Google Calendar (game)</div>
                </div>
                <div className={`rounded-lg border p-3 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className={`text-xs ${muted}`}>Practices affected</div>
                  <div className={`text-2xl font-semibold ${text}`} data-testid="preview-practices-count">{previewData.counts.practices}</div>
                  <div className={`text-[10px] ${muted}`}>Google Calendar (practice) only — never WP</div>
                </div>
              </div>
              {/* Config sanity warnings */}
              <div className="space-y-1">
                {!previewData.config.game_calendar_id && (
                  <div className={`text-[11px] px-2 py-1 rounded ${isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    ⚠ No game Google Calendar set for {selectedSport}. Games will queue but not flush until you configure it in Settings → Google Calendars.
                  </div>
                )}
                {previewData.counts.practices > 0 && !previewData.config.practice_calendar_id && (
                  <div className={`text-[11px] px-2 py-1 rounded ${isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    ⚠ No practice Google Calendar set for {selectedSport}. Practices will queue but not flush.
                  </div>
                )}
                {previewMode !== 'unpublish' && previewData.config.referee_email_enabled && !previewData.config.referee_email && (
                  <div className={`text-[11px] px-2 py-1 rounded ${isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    ⚠ Referee email is enabled but no address is set. The agency will not receive game notices.
                  </div>
                )}
              </div>
              {/* Scrollable preview list */}
              <div className={`rounded-lg border max-h-64 overflow-y-auto ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <table className="w-full text-xs">
                  <thead className={`sticky top-0 ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <th className={`py-2 px-2 text-left ${muted}`}>Date</th>
                      <th className={`py-2 px-2 text-left ${muted}`}>Opponent / Type</th>
                      <th className={`py-2 px-2 text-left ${muted}`}>Teams</th>
                      <th className={`py-2 px-2 text-left ${muted}`}>Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.games.map(g => (
                      <tr key={`g-${g.id}`} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200/60'}`}>
                        <td className={`py-1.5 px-2 font-mono ${text}`}>{g.date}</td>
                        <td className={`py-1.5 px-2 ${text}`}>{g.opponent} <span className={muted}>({g.home_away})</span></td>
                        <td className={`py-1.5 px-2 ${muted}`}>{g.teams || '—'}</td>
                        <td className="py-1.5 px-2">
                          <Badge className="bg-emerald-600 text-[10px]">WP</Badge>
                          <Badge className="bg-blue-600 text-[10px] ml-1">GCal·game</Badge>
                        </td>
                      </tr>
                    ))}
                    {previewData.practices.map(p => (
                      <tr key={`p-${p.id}`} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200/60'}`}>
                        <td className={`py-1.5 px-2 font-mono ${text}`}>{p.date}</td>
                        <td className={`py-1.5 px-2 ${text}`}>{p.event_type} {p.team_slug ? <span className={muted}>· {(teamLabels[p.team_slug] || p.team_slug.toUpperCase())}</span> : null}</td>
                        <td className={`py-1.5 px-2 ${muted}`}>{p.time || 'TBD'}</td>
                        <td className="py-1.5 px-2">
                          <Badge className="bg-blue-600 text-[10px]">GCal·practice</Badge>
                        </td>
                      </tr>
                    ))}
                    {previewData.counts.games === 0 && previewData.counts.practices === 0 && (
                      <tr><td colSpan={4} className={`py-4 text-center ${muted}`}>
                        Nothing to {previewMode === 'unpublish' ? 'unpublish' : 'publish'} in the current scope.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={() => setPreviewOpen(false)} className={muted} data-testid="preview-cancel">
                  Cancel
                </Button>
                <Button onClick={confirmPublish}
                  disabled={previewData.counts.games === 0 && previewData.counts.practices === 0}
                  className={previewMode === 'unpublish'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-amber-600 hover:bg-amber-700 text-white'}
                  data-testid="preview-confirm">
                  {previewMode === 'unpublish'
                    ? <><XCircle className="h-4 w-4 mr-1" />Confirm Unpublish</>
                    : <><Send className="h-4 w-4 mr-1" />Confirm Publish</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Shared import tool — the IDENTICAL component the Tech Specialist mounts; no role-specific
          behaviour. defaultEntity="games" is just the sensible opening selection for this page. */}
      <OlImporter
        open={showImport}
        onOpenChange={setShowImport}
        orgId={orgId}
        defaultEntity="games"
        onCommitted={() => fetchSchedule()}
      />
    </div>
  );
}
